import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { getAuth } from 'firebase/auth';
import { IoAdd, IoClose, IoSearch, IoArrowForward, IoCaretUp, IoCaretDown, IoSaveOutline, IoSearchOutline, IoGridOutline, IoBarChartOutline, IoDownloadOutline } from 'react-icons/io5';
import { exportZipModeResults } from '../utils/exportToExcel';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import AccordionSection from '../components/shared/AccordionSection';
import InputGroup from '../components/shared/InputGroup';
import FormField from '../components/shared/FormField';
import { FieldTooltip } from '../components/InfoBubble';
import api from '../api/api';
import logger from '../logger/logger';
import { initialFormData } from '../data/inputData';
import { devices } from '../data/constants';
import { usePayments } from '../hooks/usePayments';
import { ModalBackgroundStyled } from '../styles/Main';
import QuirkyLoadingMessage from '../components/QuirkyLoadingMessage';

// CapEx Constants (from MF_CAPEX_PLAN.md Section 18 - Canonical Cost Model)
const CAPEX_BASE_COSTS = {
  roof: 2000, hvac: 4500, plumbing: 2000, water_heater: 1000,
  appliances: 2000, flooring: 400, driveway: 3500, windows: 1000,
  paint: 300, cabinets: 3000, structure: 5000, components: 800,
  landscaping: 1200, other: 0
};

const CAPEX_RATES = {
  roof: 3.00, hvac: 3.50, plumbing: 1.50, water_heater: 0,
  appliances: 0, flooring: 4.00, driveway: 0, windows: 2.50,
  paint: 2.75, cabinets: 0, structure: 3.00, components: 0.50,
  landscaping: 0, other: 0
};

// True DIY rates - eliminates labor component from $/sqft
// Items where $/sqft is mostly labor get $0, items with material scaling keep reduced rates
const CAPEX_TRUE_DIY_RATES = {
  roof: 3.00,      // Keep - materials scale with sqft
  hvac: 0,         // Zero - $/sqft is labor (ductwork install)
  plumbing: 0,     // Zero - $/sqft is labor (running pipes)
  water_heater: 0, // Already fixed
  appliances: 0,   // Already fixed
  flooring: 2.50,  // Reduced - materials scale, remove labor portion
  driveway: 0,     // Already fixed
  windows: 0,      // Zero - $/sqft is labor (install)
  paint: 1.50,     // Reduced - paint scales, remove labor portion
  cabinets: 0,     // Already fixed
  structure: 0,    // Zero - $/sqft is labor (framing)
  components: 0.25,// Reduced - partial materials
  landscaping: 0,  // Already fixed
  other: 0
};

const CAPEX_TIER_MULTIPLIERS = {
  budget: { roof: 0.6, hvac: 0.9, plumbing: 0.85, water_heater: 0.7, appliances: 0.95, flooring: 0.6, driveway: 0.8, windows: 0.7, paint: 0.4, cabinets: 0.7, structure: 0.9, components: 0.6, landscaping: 0.5, other: 1.0 },
  // True DIY = for licensed/permitted trades (roof, HVAC, plumbing, structure, electrical), you ARE licensed so materials only.
  // For already-DIY-friendly items (paint, flooring, etc.), Budget already assumes DIY so True DIY is same or slightly lower.
  trueDiy: { roof: 0.40, hvac: 0.50, plumbing: 0.30, water_heater: 0.65, appliances: 0.90, flooring: 0.55, driveway: 0.75, windows: 0.65, paint: 0.35, cabinets: 0.65, structure: 0.55, components: 0.50, landscaping: 0.45, other: 1.0 },
  standard: { roof: 1.0, hvac: 1.0, plumbing: 1.0, water_heater: 1.0, appliances: 1.0, flooring: 1.0, driveway: 1.0, windows: 1.0, paint: 1.0, cabinets: 1.0, structure: 1.0, components: 1.0, landscaping: 1.0, other: 1.0 },
  premium: { roof: 1.5, hvac: 1.4, plumbing: 1.3, water_heater: 1.3, appliances: 1.2, flooring: 1.5, driveway: 1.4, windows: 1.4, paint: 1.6, cabinets: 1.5, structure: 1.4, components: 1.3, landscaping: 1.5, other: 1.0 }
};

const CAPEX_MF_MULTIPLIERS = {
  roof: { 2: 1.0, 3: 1.0, 4: 1.0 },
  hvac: { 2: 1.7, 3: 2.3, 4: 2.8 },
  plumbing: { 2: 1.25, 3: 1.4, 4: 1.5 },
  water_heater: { 2: 2.0, 3: 3.0, 4: 4.0 },
  appliances: { 2: 1.9, 3: 2.7, 4: 3.5 },
  flooring: { 2: 1.05, 3: 1.08, 4: 1.10 },
  driveway: { 2: 1.15, 3: 1.25, 4: 1.4 },
  windows: { 2: 1.0, 3: 1.0, 4: 1.0 },
  paint: { 2: 1.05, 3: 1.08, 4: 1.10 },
  cabinets: { 2: 1.9, 3: 2.7, 4: 3.5 },
  structure: { 2: 1.0, 3: 1.0, 4: 1.0 },
  components: { 2: 1.35, 3: 1.6, 4: 1.8 },
  landscaping: { 2: 1.0, 3: 1.0, 4: 1.0 },
  other: { 2: 1.0, 3: 1.0, 4: 1.0 }
};

const CAPEX_LIFESPANS = {
  roof: 25, hvac: 20, plumbing: 45, water_heater: 12,
  appliances: 12, flooring: 8, driveway: 40, windows: 30,
  paint: 6, cabinets: 18, structure: 50, components: 12,
  landscaping: 12, other: 10
};

// Condition determines age as percentage of lifespan (from MD Section 18)
const CONDITION_AGE_MULTIPLIERS = {
  outdated: 0.90,  // 90% of lifespan used (10% remaining) - needs replacement soon
  current: 0.50,   // 50% of lifespan used (50% remaining) - mid-life
  new: 0.10        // 10% of lifespan used (90% remaining) - recently replaced
};

// Human-readable labels for CapEx items
const CAPEX_LABELS = {
  roof: 'Roof', hvac: 'HVAC', plumbing: 'Plumbing', water_heater: 'Water Heater',
  appliances: 'Appliances', flooring: 'Flooring', driveway: 'Driveway', windows: 'Windows',
  paint: 'Paint', cabinets: 'Cabinets/Counters', structure: 'Structure', components: 'Components',
  landscaping: 'Landscaping', other: 'Other'
};

const CAPEX_ITEMS = Object.keys(CAPEX_BASE_COSTS);

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;

  @media only screen and ${devices.md} {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0;
`;

// ZIP Manager Styles
const ZipContainer = styled.div`
  margin-bottom: 16px;
`;

// Property Type Selector Styles
const PropertyTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PropertyTypeDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0;
  line-height: 1.5;
`;

const PropertyTypeOptions = styled.div`
  display: flex;
  gap: 16px;
  
  @media only screen and ${devices.md} {
    flex-direction: column;
  }
`;

const PropertyTypeCard = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 20px;
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor + '15' : 'transparent'};
  border: 2px solid ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.activeColor};
    background: ${({ theme }) => theme.colors.activeColor + '08'};
  }
`;

const PropertyTypeIcon = styled.div`
  font-size: 32px;
`;

const PropertyTypeLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.cardText};
`;

const PropertyTypeSubtext = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.subTextColor};
  text-align: center;
`;

const ZipInputRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  @media only screen and ${devices.md} {
    flex-direction: column;
  }
`;

const ZipInput = styled.input`
  flex: 1;
  max-width: 200px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  font-size: 14px;
  font-family: ${({ theme }) => theme.colors.ff};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.activeColor};
  }

  @media only screen and ${devices.md} {
    max-width: 100%;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.activeColorDarkened};
  }
`;

const ZipChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ZipChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cardText};
`;

const RemoveChipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  background: ${({ theme }) => theme.colors.subTextColor};
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.errorColor};
  }
`;

const NoZipsMessage = styled.p`
  color: ${({ theme }) => theme.colors.subTextColor};
  font-size: 14px;
  margin: 0;
`;

// Remember Values Toggle
const RememberToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const RememberToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.cardText};
  cursor: pointer;
  user-select: none;
`;

const RememberToggleCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.activeColor};
`;

const RememberToggleHint = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin-left: auto;
`;

// CapEx Explanation Box (matches Manual mode style)
const CapExExplanation = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.activeColor}10;
  border: 1px solid ${({ theme }) => theme.colors.activeColor}30;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.cardText};

  strong {
    color: ${({ theme }) => theme.colors.activeColor};
  }

  p {
    margin: 0 0 10px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }
`;

// CapEx Tier Selector Styles
const TierSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TierSelectorLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.cardText};
`;

const TierButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 8px;
  padding: 4px;
`;

const TierDescription = styled.span`
  font-size: 13px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin-left: 8px;
`;

const TierButton = styled.button`
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.colors.ff};
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.cardBG};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.cardText};

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const TrueDiyToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px dashed ${({ theme }) => theme.colors.borderColor};
  border-radius: 6px;
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
  
  label {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.cardText};
    cursor: pointer;
  }
`;

const ConditionSelectorContainer = styled.div`
  margin-bottom: 16px;
`;

const ConditionButtonGroup = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 8px;
  padding: 4px;
`;

const ConditionButton = styled.button`
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.colors.ff};
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.cardBG};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.cardText};

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const CapExItemRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding: 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};

  &:last-child {
    border-bottom: none;
  }
`;

const CapExItemLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.cardText};
  min-width: 120px;
  flex: 1;
`;

const CapExRateDisplay = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.subTextColor};
  width: 70px;
  text-align: right;
`;

const CapExItemInput = styled.input`
  width: 80px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 6px;
  font-size: 13px;
  font-family: ${({ theme }) => theme.colors.ff};
  text-align: right;
  background: ${({ theme }) => theme.colors.inputBg};
  color: ${({ theme }) => theme.colors.cardText};
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const CapExConditionSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 6px;
  font-size: 12px;
  font-family: ${({ theme }) => theme.colors.ff};
  background: ${({ theme }) => theme.colors.inputBg};
  color: ${({ theme }) => theme.colors.cardText};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

// Action Button
const RunAnalysisButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  margin: 24px auto;
  padding: 16px 32px;
  background: ${({ theme, disabled }) => disabled ? theme.colors.themeActiveDisabled : theme.colors.activeColor};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  font-family: ${({ theme }) => theme.colors.ff};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme, disabled }) => disabled ? theme.colors.themeActiveDisabled : theme.colors.activeColorDarkened};
  }
`;

// Query Limit Modal Styles
const QueryLimitModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const QueryLimitTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.cardText};
  margin: 0 0 12px 0;
`;

const QueryLimitMessage = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const QueryLimitButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const QueryLimitButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${({ theme }) => theme.colors.ff};
  
  ${({ primary, theme }) => primary ? `
    background: ${theme.colors.activeColor};
    color: #fff;
    border: none;
    
    &:hover {
      background: ${theme.colors.activeColorDarkened};
    }
  ` : `
    background: transparent;
    color: ${theme.colors.cardText};
    border: 1px solid ${theme.colors.borderColor};
    
    &:hover {
      background: ${theme.colors.opacityColor};
    }
  `}
`;

// Results Section
const ResultsContainer = styled.div`
  margin-top: 8px;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.warningColor}15;
  border: 1px solid ${({ theme }) => theme.colors.warningColor};
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textColor};

  strong {
    color: ${({ theme }) => theme.colors.warningColor};
  }
`;

// Sortable Results Table
const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-top: 16px;
  table-layout: auto;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  overflow: hidden;
  
  @media only screen and ${devices.md} {
    display: block;
    overflow-x: auto;
  }
`;

const ResultsTableHead = styled.thead`
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  
  tr {
    display: table-row;
  }
  
  th {
    padding: 12px 6px;
    text-align: center;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;
    border-right: 1px solid rgba(255,255,255,0.1);
    
    &:last-child {
      border-right: none;
    }
    
    &:hover {
      background: ${({ theme }) => theme.colors.activeColorDarkened};
    }
  }
  
  th:first-child {
    text-align: left;
    min-width: 180px;
  }
`;

const SortIndicator = styled.span`
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  opacity: ${({ $active }) => $active ? 1 : 0.4};
`;

const ResultsTableBody = styled.tbody`
  tr {
    display: table-row;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    background: ${({ theme }) => theme.colors.cardBG};
    transition: background 0.2s ease;
    
    &:nth-child(even) {
      background: ${({ theme }) => theme.colors.opacityColor};
    }
    
    &:hover {
      background: ${({ theme }) => theme.colors.lightActiveHover};
    }
  }
  
  td {
    padding: 12px 6px;
    text-align: center;
    vertical-align: middle;
    color: ${({ theme }) => theme.colors.cardText};
  }
  
  td:first-child {
    text-align: left;
  }
`;

const AddressLink = styled.a`
  color: ${({ theme }) => theme.colors.activeColor};
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Details expand button (magnifying glass)
const DetailsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin-left: 6px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 4px;
  color: ${({ theme, $expanded }) => $expanded ? theme.colors.activeColor : theme.colors.subTextColor};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.opacityColor};
    color: ${({ theme }) => theme.colors.activeColor};
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

// Expandable details row
const DetailsRow = styled.tr`
  background: ${({ theme }) => theme.colors.opacityColor};
  
  td {
    padding: 0 !important;
  }
`;

const DetailsContent = styled.div`
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
  width: 100%;
  box-sizing: border-box;
  
  @media only screen and ${devices.md} {
    padding: 12px;
  }
`;

const InsightsPanel = styled.div`
  background: ${({ theme }) => theme.colors.opacityColor};
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderColor};
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  
  @media only screen and ${devices.md} {
    grid-template-columns: repeat(2, 1fr);
    padding: 12px;
    gap: 12px;
  }
  
  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const DetailsTab = styled.button`
  padding: 8px 16px;
  background: ${({ theme, $active }) => $active ? theme.colors.activeColor : 'transparent'};
  color: ${({ theme, $active }) => $active ? '#fff' : theme.colors.cardText};
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme, $active }) => $active ? theme.colors.activeColor : theme.colors.opacityColor};
  }
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.cardBG};
  
  th {
    padding: 10px 8px;
    text-align: left;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    color: #ffffff;
    background: ${({ theme }) => theme.colors.activeColor};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    color: ${({ theme }) => theme.colors.cardText};
    background: ${({ theme }) => theme.colors.cardBG};
    white-space: nowrap;
  }
  
  tbody tr {
    transition: background 0.2s ease;
    
    &:hover {
      background: ${({ theme }) => theme.colors.opacityColor};
    }
  }
`;

const DetailsTableWrapper = styled.div`
  max-height: 250px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
`;

const DetailsViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: ${({ theme }) => theme.colors.opacityColor};
  border-radius: 8px;
  padding: 4px;
  margin-left: auto;
`;

const DetailsViewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: ${({ $active, theme }) => $active ? theme.colors.activeColor : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.subTextColor};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.activeColor : theme.colors.borderColor};
  }
`;

const DetailsChartContainer = styled.div`
  height: 250px;
  margin: 8px 0;
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ theme }) => theme.colors.successColor};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const ExportDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.colors.cardBG};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  padding: 12px;
  min-width: 220px;
  z-index: 100;
`;

const ExportCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.cardText};
  cursor: pointer;
  margin-bottom: 12px;
  
  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ExportConfirmButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  
  &:hover {
    opacity: 0.9;
  }
`;

// Editable Market Data Section
const MarketDataSection = styled.div`
  margin-bottom: 16px;
`;

const MarketDataDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const UtilitiesTip = styled.span`
  display: block;
  margin-top: 8px;
  font-weight: 600;
  font-style: italic;
  color: ${({ theme }) => theme.colors.activeColor};
`;

const ResetButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const ResetButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: ${({ theme }) => theme.colors.activeColor};
  border: 1px solid ${({ theme }) => theme.colors.activeColor};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.activeColor}10;
  }
`;

const EditableTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  @media only screen and ${devices.md} {
    display: block;
    overflow-x: auto;
  }
`;

const EditableTableHead = styled.thead`
  background: ${({ theme }) => theme.colors.activeColor};
  color: #fff;
  
  th {
    padding: 10px 8px;
    text-align: center;
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
  }
  
  th:first-child {
    text-align: left;
  }
`;

const EditableTableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor};
    background: ${({ theme }) => theme.colors.cardBG};
    
    &:hover {
      background: ${({ theme }) => theme.colors.opacityColor};
    }
  }
  
  td {
    padding: 8px;
    text-align: center;
    vertical-align: middle;
    color: ${({ theme }) => theme.colors.cardText};
  }
  
  td:first-child {
    text-align: left;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.cardText};
  }
`;

const FetchedValue = styled.div`
  font-size: 11px;
  color: ${({ theme, $isInvalid }) => $isInvalid ? theme.colors.errorColor : theme.colors.subTextColor};
  margin-bottom: 4px;
  font-weight: ${({ $isInvalid }) => $isInvalid ? '600' : 'normal'};
`;

const EditableInput = styled.input`
  width: 80px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: 4px;
  font-size: 13px;
  text-align: center;
  font-family: ${({ theme }) => theme.colors.ff};
  background: ${({ theme }) => theme.colors.inputBg};
  color: ${({ theme }) => theme.colors.cardText};
  -moz-appearance: textfield;
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ResultsTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.cardText};
`;

const ResultsCount = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.subTextColor};
`;

const ZipModePage = ({ user, onBack }) => {
  // Theme for dynamic colors
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Payments hook for query limit checking
  const { updateQueryLimit, checkQueryLimit, getCustomer, getCurrentPlan, getProducts, manageSubscription } = usePayments();
  
  // Query limit modal state
  const [showQueryLimitModal, setShowQueryLimitModal] = useState(false);
  const [noSubscription, setNoSubscription] = useState(false);
  const [freeResetDate, setFreeResetDate] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Fetch subscription info on mount
  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      if (user?.uid) {
        const [sub, cust, prods] = await Promise.all([
          getCurrentPlan(user.uid),
          getCustomer(user.uid),
          getProducts()
        ]);
        setSubscription(sub);
        setCustomer(cust);
        if (prods) {
          setPlans(prods);
        }
      }
    };
    fetchSubscriptionInfo();
  }, [user?.uid, getCurrentPlan, getCustomer, getProducts]);
  
  // Format reset date
  const getResetInfo = () => {
    if (!subscription?.current_period_end_date) return null;
    
    // Find the plan by priceId (more reliable than planId)
    const currentPriceId = subscription?.plan?.price?.id || customer?.priceId;
    const currentPlan = plans.find(p => p.prices?.priceId === currentPriceId);
    
    const planName = currentPlan?.name || 'your plan';
    const queries = currentPlan?.metadata?.queries || currentPlan?.up_to || '0';
    const queryDisplay = queries === '-1' || queries === -1 ? 'unlimited' : queries;
    
    return {
      date: subscription.current_period_end_date,
      queries: queryDisplay,
      planName
    };
  };
  
  // State for accordion sections
  const [expandedSection, setExpandedSection] = useState(1);
  
  // Property type state (single_family or multi_family)
  const [propertyType, setPropertyType] = useState('single_family');
  
  // ZIP codes state
  const [zipCodes, setZipCodes] = useState([]);
  const [zipsLoading, setZipsLoading] = useState(true);
  const [newZip, setNewZip] = useState('');
  const [marketData, setMarketData] = useState({});
  
  // Editable market data (user can override fetched values)
  const [editableMarketData, setEditableMarketData] = useState({});
  const [fetchedMarketData, setFetchedMarketData] = useState({}); // Original fetched values for reference
  
  // CapEx tier and condition state
  const [capexTier, setCapexTier] = useState('standard');
  const [trueDiyMode, setTrueDiyMode] = useState(false); // Toggle for licensed/all-DIY users
  const [globalCondition, setGlobalCondition] = useState('current');
  const [itemConditions, setItemConditions] = useState({}); // Per-item condition overrides
  const [capexCosts, setCapexCosts] = useState(() => {
    // Initialize with default costs (will be recalculated when market data arrives)
    const defaultCosts = {};
    CAPEX_ITEMS.forEach(item => {
      defaultCosts[item] = CAPEX_BASE_COSTS[item] + (1200 * CAPEX_RATES[item]); // Assume 1200 sqft default
    });
    return defaultCosts;
  });
  
  // Default parameter values
  const defaultParams = {
    // Loan Terms
    loanAmount: 75,
    interestRate: 7.0,
    loanTerm: 30,
    amortizationPeriod: 30,
    interestOnlyPeriod: 0,
    // Operating Expenses
    vacancy: 5,
    managementFee: 8,
    insurance: 1200,
    mortgageInsurance: 0,
    advertising: 0,
    taxService: 0,
    otherExpenses: 0,
    // Market Assumptions
    rentIncrease: 2,
    expenseIncrease: 2,
    // CapEx (ages)
    roofAge: 10,
    hvacAge: 10,
    plumbingAge: 10,
    waterHeaterAge: 5,
    appliancesAge: 5,
    flooringAge: 3,
    // CapEx (costs)
    roofCost: 8000,
    hvacCost: 5000,
    plumbingCost: 3000,
    waterHeaterCost: 1200,
    appliancesCost: 2000,
    flooringCost: 3000,
  };
  
  // Remember values toggle state (persisted to localStorage)
  const [rememberValues, setRememberValues] = useState(() => {
    try {
      return localStorage.getItem('propninja_remember_zip') === 'true';
    } catch {
      return false;
    }
  });
  
  // Load saved values from localStorage if remember is enabled
  const loadSavedParams = useCallback(() => {
    if (!rememberValues) return defaultParams;
    try {
      const saved = localStorage.getItem('propninja_zip_params');
      if (saved) {
        return { ...defaultParams, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('Failed to load saved form values:', e);
    }
    return defaultParams;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rememberValues]);
  
  // Parameters state
  const [params, setParams] = useState(() => loadSavedParams());
  
  // Screening threshold (also persisted)
  const loadSavedScreening = useCallback(() => {
    if (!rememberValues) return { requiredCOC: 8, cocTerm: 5, holdPeriod: 6 };
    try {
      const saved = localStorage.getItem('propninja_zip_screening');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Backward compat: if holdPeriod missing, default to cocTerm or 6
        if (!parsed.holdPeriod) parsed.holdPeriod = parsed.cocTerm || 6;
        return parsed;
      }
    } catch (e) {
      console.warn('Failed to load saved screening values:', e);
    }
    return { requiredCOC: 8, cocTerm: 5, holdPeriod: 6 };
  }, [rememberValues]);
  
  const [requiredCOC, setRequiredCOC] = useState(() => loadSavedScreening().requiredCOC);
  const [cocTerm, setCocTerm] = useState(() => loadSavedScreening().cocTerm);
  const [holdPeriod, setHoldPeriod] = useState(() => loadSavedScreening().holdPeriod);
  
  // Save to localStorage when values change (if remember is enabled)
  useEffect(() => {
    if (rememberValues) {
      try {
        localStorage.setItem('propninja_zip_params', JSON.stringify(params));
        localStorage.setItem('propninja_zip_screening', JSON.stringify({ requiredCOC, cocTerm, holdPeriod }));
      } catch (e) {
        console.warn('Failed to save form values:', e);
      }
    }
  }, [params, requiredCOC, cocTerm, holdPeriod, rememberValues]);
  
  // Save market data overrides when editableMarketData changes (always, independent of remember checkbox)
  useEffect(() => {
    if (Object.keys(editableMarketData).length > 0 && Object.keys(fetchedMarketData).length > 0) {
      try {
        // Only save fields that differ from fetched values
        const overrides = {};
        Object.keys(editableMarketData).forEach(zip => {
          const edited = editableMarketData[zip] || {};
          const fetched = fetchedMarketData[zip] || {};
          const zipOverrides = {};
          Object.keys(edited).forEach(field => {
            if (edited[field] !== fetched[field]) {
              zipOverrides[field] = edited[field];
            }
          });
          if (Object.keys(zipOverrides).length > 0) {
            overrides[zip] = zipOverrides;
          }
        });
        if (Object.keys(overrides).length > 0) {
          localStorage.setItem('propninja_zip_market_overrides', JSON.stringify(overrides));
        } else {
          localStorage.removeItem('propninja_zip_market_overrides');
        }
      } catch (e) {
        console.warn('Failed to save market data overrides:', e);
      }
    }
  }, [editableMarketData, fetchedMarketData]);
  
  // Handle remember toggle change
  const handleRememberToggle = (enabled) => {
    setRememberValues(enabled);
    try {
      localStorage.setItem('propninja_remember_zip', String(enabled));
      if (!enabled) {
        // Clear saved values when disabling (market overrides are independent)
        localStorage.removeItem('propninja_zip_params');
        localStorage.removeItem('propninja_zip_screening');
      }
    } catch (e) {
      console.warn('Failed to save remember preference:', e);
    }
  };
  
  // Recalculate CapEx display costs when tier or trueDiyMode changes
  // Display shows BASE COST ONLY (no sqft scaling) - backend handles per-property sqft scaling
  useEffect(() => {
    // Use trueDiy multipliers when budget is selected and trueDiyMode is on
    const effectiveTier = (capexTier === 'budget' && trueDiyMode) ? 'trueDiy' : capexTier;
    
    const newCosts = {};
    CAPEX_ITEMS.forEach(item => {
      const base = CAPEX_BASE_COSTS[item];
      const tierMult = CAPEX_TIER_MULTIPLIERS[effectiveTier][item];
      // Display only base cost × tier multiplier (sqft scaling happens per-property in backend)
      const cost = base * tierMult;
      newCosts[item] = Math.round(cost);
    });
    
    setCapexCosts(newCosts);
  }, [capexTier, trueDiyMode]);
  
  // Handle per-item condition change
  const handleItemConditionChange = (item, condition) => {
    if (condition === globalCondition) {
      // Remove override if setting to global value
      setItemConditions(prev => {
        const newConditions = { ...prev };
        delete newConditions[item];
        return newConditions;
      });
    } else {
      setItemConditions(prev => ({ ...prev, [item]: condition }));
    }
  };
  
  // Handle CapEx cost override
  const handleCapexCostChange = (item, value) => {
    setCapexCosts(prev => ({ ...prev, [item]: parseFloat(value) || 0 }));
  };
  
  // Get effective condition for an item (override or global)
  const getItemCondition = (item) => itemConditions[item] || globalCondition;
  
  // Calculate age from condition and lifespan
  const getCapexAge = (item) => {
    const condition = getItemCondition(item);
    const lifespan = CAPEX_LIFESPANS[item];
    return Math.round(lifespan * CONDITION_AGE_MULTIPLIERS[condition]);
  };
  
  // Select all on focus for easy replacement (works in both modes)
  const handleFieldFocus = useCallback((e) => {
    e.target.select();
  }, []);
  
  // Loading/status
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessages, setStatusMessages] = useState('');
  const [loadingError, setLoadingError] = useState(null);
  const evtSourceRef = useRef(null);
  
  // Progress bar state
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('');
  const progressTimerRef = useRef(null);
  const progressStateRef = useRef({ completed: 0, total: 100 });
  
  // Results
  const [results, setResults] = useState(null);
  
  // Sort state for results table
  const [sortConfig, setSortConfig] = useState({ key: 'coc_percentage_at_year', direction: 'desc' });
  
  // Expanded property details state (financial details - chart icon)
  const [expandedPropertyId, setExpandedPropertyId] = useState(null);
  const [detailsTab, setDetailsTab] = useState('cashflow');
  const [detailsViewMode, setDetailsViewMode] = useState('table'); // 'table' | 'chart'
  
  // Expanded insights state (insights panel - magnifying glass icon)
  const [insightsPropertyId, setInsightsPropertyId] = useState(null);
  
  // Export state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportIncludeDetails, setExportIncludeDetails] = useState(false);
  const [exportIncludeInputs, setExportIncludeInputs] = useState(false);

  // Cleanup EventSource and progress timer on unmount
  useEffect(() => {
    return () => {
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
      }
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }
    };
  }, []);

  // Load user's ZIP codes on mount
  useEffect(() => {
    if (user?.uid) {
      setZipsLoading(true);
      api.post('/get-zip-codes', { user: user.uid })
        .then((response) => setZipCodes(response.data))
        .catch((err) => logger.error('Error loading ZIP codes: ' + err))
        .finally(() => setZipsLoading(false));
    } else {
      setZipsLoading(false);
    }
  }, [user]);

  // Keyboard shortcut: Ctrl+Enter to run analysis
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        const runButton = document.querySelector('[data-run-analysis]');
        if (runButton && !runButton.disabled) {
          runButton.click();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ZIP code handlers
  const handleAddZip = () => {
    if (!newZip.trim() || !/^\d{5}$/.test(newZip.trim())) return;
    
    api.post('/add-zip-code', { zip_code: newZip.trim(), user: user.uid })
      .then(() => api.post('/get-zip-codes', { user: user.uid }))
      .then((resp) => {
        setZipCodes(resp.data);
        setNewZip('');
      })
      .catch((err) => logger.error('Error adding ZIP code: ' + err));
  };

  const handleRemoveZip = (zipToRemove) => {
    api.post('/delete-zip-code', { zip_code: zipToRemove, user: user.uid })
      .then(() => api.post('/get-zip-codes', { user: user.uid }))
      .then((resp) => setZipCodes(resp.data))
      .catch((err) => logger.error('Error removing ZIP code: ' + err));
  };

  const handleParamChange = (field, value) => {
    setParams(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  // Build summary text for collapsed sections
  const getMarketsSummary = () => {
    if (zipCodes.length === 0) return 'No ZIP codes added';
    return `${zipCodes.join(', ')} (${zipCodes.length} market${zipCodes.length > 1 ? 's' : ''})`;
  };

  const getMarketDataSummary = () => {
    const zipCount = Object.keys(editableMarketData).length;
    if (zipCount === 0) return 'No data fetched yet';
    return `Data for ${zipCount} ZIP${zipCount > 1 ? 's' : ''} ready for analysis`;
  };

  const handleEditableDataChange = (zip, field, value) => {
    setEditableMarketData(prev => ({
      ...prev,
      [zip]: {
        ...prev[zip],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const getParametersSummary = () => {
    const totalCapEx = Object.values(capexCosts).reduce((sum, cost) => sum + (cost || 0), 0);
    const tierLabel = capexTier.charAt(0).toUpperCase() + capexTier.slice(1);
    return `${params.loanAmount}% LTV, ${params.interestRate}% rate, ${tierLabel} CapEx ($${(totalCapEx/1000).toFixed(0)}K)`;
  };

  const getScreeningSummary = () => {
    return `${requiredCOC}% COC by yr ${cocTerm}, ${holdPeriod}yr hold`;
  };

  // Parse SSE message and update progress
  const updateProgressFromMessage = (message, totalZips) => {
    const state = progressStateRef.current;
    const isMF = propertyType === 'multi_family';
    
    // Weight distribution (total = 100)
    const WEIGHTS = {
      gemini: 40,           // Gemini market data
      listingsPerZip: 8,    // Listings per ZIP
      rentcastPerZip: 2,    // RentCast per ZIP  
      parsingPerZip: 10,    // Description parsing per ZIP (both SF and MF)
    };
    
    // Calculate dynamic total based on ZIP count
    state.total = WEIGHTS.gemini + 
                  (WEIGHTS.listingsPerZip * totalZips) + 
                  (WEIGHTS.rentcastPerZip * totalZips) + 
                  (WEIGHTS.parsingPerZip * totalZips);
    
    // Parse message and update completed weight
    if (message.includes('Skipped') && message.includes('Gemini')) {
      state.completed += WEIGHTS.gemini;
      setProgressStatus('Using cached market data...');
    } else if (message.includes('[TIMING] Gemini market data') || message.includes('Fetched new Gemini data') || message.includes('Fetched new MF Gemini data')) {
      if (!state.geminiDone) {
        state.completed += WEIGHTS.gemini;
        state.geminiDone = true;
        setProgressStatus('Analyzing market conditions...');
      }
    } else if (message.includes('Skipped') && message.includes('listings')) {
      state.completed += WEIGHTS.listingsPerZip;
      const zipMatch = message.match(/ZIP (\d{5})/);
      setProgressStatus(`Using cached listings${zipMatch ? ` for ${zipMatch[1]}` : ''}...`);
    } else if (message.includes('Fetched and upserted') && message.includes('listings')) {
      state.completed += WEIGHTS.listingsPerZip;
      const zipMatch = message.match(/ZIP (\d{5})/);
      setProgressStatus(`Fetched listings${zipMatch ? ` for ${zipMatch[1]}` : ''}...`);
    } else if (message.includes('Skipped') && message.includes('RentCast')) {
      state.completed += WEIGHTS.rentcastPerZip;
    } else if (message.includes('Fetched new') && message.includes('RentCast')) {
      state.completed += WEIGHTS.rentcastPerZip;
    } else if (message.includes('Parsing') && message.includes('descriptions')) {
      const zipMatch = message.match(/ZIP (\d{5})/);
      const countMatch = message.match(/Parsing (\d+)/);
      const count = countMatch ? countMatch[1] : '';
      setProgressStatus(`Analyzing ${count} properties${zipMatch ? ` in ${zipMatch[1]}` : ''}...`);
    } else if (message.includes('_parse_progress:')) {
      // Handle incremental parsing progress: sf_parse_progress:10/46:67218:5.2s or mf_parse_progress:10/46:67218:5.2s
      const match = message.match(/(\d+)\/(\d+):(\d{5})(?::(\d+\.?\d*)s)?/);
      if (match) {
        const [, current, total, zip, elapsed] = match;
        const timeStr = elapsed ? ` (${elapsed}s)` : '';
        setProgressStatus(`Analyzing ${current}/${total} properties in ${zip}${timeStr}...`);
        // Add fractional progress within the parsing weight
        const fraction = parseInt(current) / parseInt(total);
        const partialWeight = WEIGHTS.parsingPerZip * fraction;
        // Store per-zip parsing progress to avoid double-counting
        if (!state.parsingProgress) state.parsingProgress = {};
        state.parsingProgress[zip] = partialWeight;
      }
    } else if (message.includes('Parsed') && message.includes('descriptions')) {
      // Clear partial progress for this ZIP and add full weight
      const zipMatch = message.match(/ZIP (\d{5})/);
      if (zipMatch && state.parsingProgress && state.parsingProgress[zipMatch[1]]) {
        delete state.parsingProgress[zipMatch[1]];
      }
      state.completed += WEIGHTS.parsingPerZip;
      setProgressStatus('Processing property details...');
    } else if (message.includes('Completed') && message.includes('processing')) {
      // Track completed ZIPs - only show "Wrapping up" when all are done
      state.zipsCompleted = (state.zipsCompleted || 0) + 1;
      const zipMatch = message.match(/ZIP (\d{5})/);
      if (state.zipsCompleted >= totalZips) {
        setProgressStatus('Wrapping up...');
      } else {
        setProgressStatus(`Processed ${state.zipsCompleted}/${totalZips} ZIP codes...`);
      }
    }
    
    // Calculate percentage including partial parsing progress (cap at 95% until final)
    const partialParsingProgress = state.parsingProgress 
      ? Object.values(state.parsingProgress).reduce((sum, val) => sum + val, 0) 
      : 0;
    const totalCompleted = state.completed + partialParsingProgress;
    const pct = Math.min(95, Math.round((totalCompleted / state.total) * 100));
    setProgress(pct);
  };

  // Fetch market data and run analysis
  const runAnalysis = async () => {
    if (zipCodes.length === 0) return;
    
    // Show loading immediately for responsive UI
    setIsLoading(true);
    
    // Check query limit before running analysis
    const queryCheck = await checkQueryLimit(user.uid);
    if (!queryCheck.allowed) {
      setNoSubscription(queryCheck.noSubscription || false);
      setFreeResetDate(queryCheck.freeResetDate || null);
      setShowQueryLimitModal(true);
      setIsLoading(false);
      return;
    }
    setStatusMessages('');
    setLoadingError(null);
    setExpandedSection(null); // Collapse all during loading
    
    // Reset progress state
    setShowProgress(false);
    setProgress(0);
    setProgressStatus('');
    progressStateRef.current = { completed: 0, total: 100, geminiDone: false, zipsCompleted: 0 };
    
    // Clear any existing timer
    if (progressTimerRef.current) {
      clearTimeout(progressTimerRef.current);
    }
    
    // Show progress bar after 1 second if still loading
    progressTimerRef.current = setTimeout(() => {
      setShowProgress(true);
      setProgressStatus('Gathering market data...');
    }, 1000);
    
    // Close any existing connection before starting new one
    if (evtSourceRef.current) {
      evtSourceRef.current.close();
    }
    // Get Firebase ID token for authentication
    let token = '';
    try {
      const auth = getAuth();
      if (auth.currentUser) {
        token = await auth.currentUser.getIdToken();
      } else {
        logger.error('No Firebase user logged in');
        setLoadingError('Please log in to fetch market data.');
        setIsLoading(false);
        if (progressTimerRef.current) {
          clearTimeout(progressTimerRef.current);
        }
        return;
      }
    } catch (error) {
      logger.error('Error getting Firebase token:', error);
      setLoadingError('Authentication error. Please try logging in again.');
      setIsLoading(false);
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }
      return;
    }
    
    const zipParam = zipCodes.join(',');
    const endpoint = propertyType === 'multi_family' ? 'stream-initial-data-mf' : 'stream-initial-data';
    evtSourceRef.current = new EventSource(
      `${api.defaults.baseURL}/${endpoint}?zip_codes=${zipParam}&token=${token}`
    );

    evtSourceRef.current.onmessage = async (event) => {
      // Capture ref at start to avoid race conditions with async operations
      const currentEvtSource = evtSourceRef.current;
      
      if (event.data.startsWith('final:')) {
        // Close connection immediately before any async work
        if (currentEvtSource) {
          currentEvtSource.close();
          evtSourceRef.current = null;
        }
        
        const payloadStr = event.data.replace('final:', '');
        try {
          const payload = JSON.parse(payloadStr);
          
          // Only charge query if fetch was successful (has Gemini data AND listings)
          if (payload.success) {
            const updateResult = await updateQueryLimit(user.uid);
            if (!updateResult.success) {
              logger.warn('Failed to decrement query limit after successful fetch');
            }
          } else {
            // Fetch failed - don't charge, show reason
            logger.info(`Fetch not charged: ${payload.refund_reason}`);
          }
          
          // Store fetched data for display/editing, but don't run calculation yet
          storeFetchedData(payload.zip_data);
          
          // Clear progress timer and complete
          if (progressTimerRef.current) {
            clearTimeout(progressTimerRef.current);
          }
          setProgress(100);
          setProgressStatus(payload.success ? 'Complete!' : `Complete (${payload.refund_reason} - no query charged)`);
          setTimeout(() => {
            setShowProgress(false);
            setIsLoading(false);
            setExpandedSection('marketData'); // Open the editable market data section
          }, 300);
        } catch (error) {
          logger.error('Error parsing final payload: ' + error);
          if (progressTimerRef.current) {
            clearTimeout(progressTimerRef.current);
          }
          setShowProgress(false);
          setIsLoading(false);
        }
      } else {
        setStatusMessages(prev => prev + event.data + '\n');
        // Update progress based on message
        updateProgressFromMessage(event.data, zipCodes.length);
      }
    };

    evtSourceRef.current.onerror = (err) => {
      logger.error('EventSource failed:', err);
      setLoadingError('Connection failed. Please check your network and try again.');
      if (evtSourceRef.current) {
        evtSourceRef.current.close();
        evtSourceRef.current = null;
      }
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }
      setShowProgress(false);
      setIsLoading(false);
    };
  };

  // Store fetched data and make it editable
  const storeFetchedData = (zipData) => {
    if (!zipData || zipData.length === 0) return;

    const newMarketData = {};
    const newEditableData = {};
    const newFetchedData = {};

    zipData.forEach((item) => {
      // Handle both SF and MF field names
      const capRate = item.financial_metrics?.average_cap_rate_mf 
        || item.financial_metrics?.average_cap_rate_b_c_properties 
        || 7;
      const taxRate = item.financial_metrics?.property_tax_rate_percentage || 1.5;
      // MF uses landlord insurance, SF uses homeowners insurance
      const insurance = item.financial_metrics?.average_landlord_insurance_cost 
        || item.financial_metrics?.average_homeowners_insurance_cost 
        || 1200;
      // MF returns monthly_average_utility_cost, SF returns annual
      const utilities = item.financial_metrics?.monthly_average_utility_cost 
        ? item.financial_metrics.monthly_average_utility_cost * 12 
        : (item.financial_metrics?.annual_average_utility_cost || 2400);
      const rentPerSqft = item.avg_rent_sqft || 0;
      const taxIncrease = item.financial_metrics?.annual_property_tax_increase_percentage || 2;
      
      newMarketData[item.zip_code] = {
        avgPrice: item.purchase_price || 0,
        avgRent: item.avg_rent_sqft ? (item.avg_rent_sqft * item.sqft) : 0,
        avgSqft: item.sqft || 0,
        rentPerSqft: rentPerSqft,
        taxRate: taxRate,
        insurance: insurance,
        capRate: capRate,
      };
      
      // Store original fetched values (for display reference)
      newFetchedData[item.zip_code] = {
        rentPerSqft,
        taxRate,
        insurance,
        utilities,
        taxIncrease,
        capRate,
      };
      
      // Initialize editable values with fetched data
      newEditableData[item.zip_code] = {
        rentPerSqft,
        taxRate,
        insurance,
        utilities,
        taxIncrease,
        capRate,
      };
    });

    // Merge saved overrides (always, independent of remember checkbox)
    try {
      const savedOverrides = localStorage.getItem('propninja_zip_market_overrides');
      if (savedOverrides) {
        const overrides = JSON.parse(savedOverrides);
        Object.keys(overrides).forEach(zip => {
          if (newEditableData[zip]) {
            newEditableData[zip] = { ...newEditableData[zip], ...overrides[zip] };
          }
        });
      }
    } catch (e) {
      console.warn('Failed to load saved market overrides:', e);
    }

    setMarketData(newMarketData);
    setFetchedMarketData(newFetchedData);
    setEditableMarketData(newEditableData);
  };

  // Reset editable market data to fetched values
  const resetMarketDataToFetched = () => {
    if (Object.keys(fetchedMarketData).length > 0) {
      setEditableMarketData({ ...fetchedMarketData });
      // Clear saved overrides
      try {
        localStorage.removeItem('propninja_zip_market_overrides');
      } catch (e) {
        console.warn('Failed to clear market overrides:', e);
      }
    }
  };

  // Build form data from editable market data and run calculation
  const runCalculationWithData = () => {
    setIsLoading(true);
    
    const newFormData = {};
    
    Object.keys(editableMarketData).forEach((zip) => {
      const marketInfo = marketData[zip] || {};
      const editedData = editableMarketData[zip] || {};
      
      newFormData[zip] = JSON.parse(JSON.stringify(initialFormData));
      
      // Property-specific data from market
      newFormData[zip]['sqft']['value'] = marketInfo.avgSqft || 0;
      newFormData[zip]['purchase_price']['value'] = marketInfo.avgPrice || 0;
      newFormData[zip]['unit_1_rent']['value'] = (editedData.rentPerSqft * marketInfo.avgSqft) || 0;
      newFormData[zip]['rent_per_sqft']['value'] = editedData.rentPerSqft || 0;
      
      // Editable market data
      newFormData[zip]['property_tax_rate_pct']['value'] = editedData.taxRate || 0;
      newFormData[zip]['homeowners_insurance_cost']['value'] = editedData.insurance || 0;
      newFormData[zip]['monthly_utilities_cost']['value'] = Math.round((editedData.utilities || 0) / 12);
      newFormData[zip]['annual_property_tax_increase_pct']['value'] = editedData.taxIncrease || 2;
      newFormData[zip]['cap_rate']['value'] = editedData.capRate || 7;
      
      // Loan parameters
      newFormData[zip]['loan_amount']['value'] = params.loanAmount;
      newFormData[zip]['interest_rate_pct']['value'] = params.interestRate;
      newFormData[zip]['loan_term']['value'] = params.loanTerm;
      newFormData[zip]['amortization_period']['value'] = params.amortizationPeriod;
      newFormData[zip]['interest_only_period']['value'] = params.interestOnlyPeriod || 0;
      
      // Operating expenses
      newFormData[zip]['general_vacancy']['value'] = params.vacancy;
      newFormData[zip]['management_fee']['value'] = params.managementFee;
      newFormData[zip]['mortgage_insurance']['value'] = params.mortgageInsurance || 0;
      newFormData[zip]['advertising']['value'] = params.advertising || 0;
      newFormData[zip]['tax_service']['value'] = params.taxService || 0;
      newFormData[zip]['other_expenses']['value'] = params.otherExpenses || 0;
      
      // Market assumptions
      newFormData[zip]['rent_increase_pct']['value'] = params.rentIncrease;
      newFormData[zip]['expenses_increase_pct']['value'] = params.expenseIncrease;
      
      // CapEx ages (calculated from condition × lifespan)
      // These are fallback values - backend will use capex_ages from request
      newFormData[zip]['roof_age']['value'] = getCapexAge('roof');
      newFormData[zip]['hvac_age']['value'] = getCapexAge('hvac');
      newFormData[zip]['plumbing_age']['value'] = getCapexAge('plumbing');
      newFormData[zip]['water_heater_age']['value'] = getCapexAge('water_heater');
      newFormData[zip]['appliances_age']['value'] = getCapexAge('appliances');
      newFormData[zip]['flooring_age']['value'] = getCapexAge('flooring');
      newFormData[zip]['driveway/parking_lot_age']['value'] = getCapexAge('driveway');
      newFormData[zip]['windows_age']['value'] = getCapexAge('windows');
      newFormData[zip]['paint_age']['value'] = getCapexAge('paint');
      newFormData[zip]['cabinets/counters_age']['value'] = getCapexAge('cabinets');
      newFormData[zip]['structure_age']['value'] = getCapexAge('structure');
      newFormData[zip]['components_age']['value'] = getCapexAge('components');
      newFormData[zip]['landscaping_age']['value'] = getCapexAge('landscaping');
      newFormData[zip]['other_age']['value'] = getCapexAge('other');
      
      // CapEx replacement costs (from user-editable capexCosts state)
      // These are fallback values - backend will calculate per-property using capex_config
      newFormData[zip]['roof_replacement_cost']['value'] = capexCosts.roof || 8000;
      newFormData[zip]['hvac_replacement_cost']['value'] = capexCosts.hvac || 5000;
      newFormData[zip]['plumbing_replacement_cost']['value'] = capexCosts.plumbing || 3000;
      newFormData[zip]['water_heater_replacement_cost']['value'] = capexCosts.water_heater || 1200;
      newFormData[zip]['appliances_replacement_cost']['value'] = capexCosts.appliances || 2000;
      newFormData[zip]['flooring_replacement_cost']['value'] = capexCosts.flooring || 3000;
      newFormData[zip]['driveway/parking_lot_replacement_cost']['value'] = capexCosts.driveway || 3500;
      newFormData[zip]['windows_replacement_cost']['value'] = capexCosts.windows || 5000;
      newFormData[zip]['paint_replacement_cost']['value'] = capexCosts.paint || 2000;
      newFormData[zip]['cabinets/counters_replacement_cost']['value'] = capexCosts.cabinets || 5000;
      newFormData[zip]['structure_replacement_cost']['value'] = capexCosts.structure || 10000;
      newFormData[zip]['components_replacement_cost']['value'] = capexCosts.components || 2000;
      newFormData[zip]['landscaping_replacement_cost']['value'] = capexCosts.landscaping || 1500;
      newFormData[zip]['other_replacement_cost']['value'] = capexCosts.other || 0;
      
      // Screening criteria
      newFormData[zip]['coc_pct']['value'] = requiredCOC;
      newFormData[zip]['coc_term']['value'] = cocTerm;
      newFormData[zip]['hold_period']['value'] = holdPeriod;
    });
    
    runCalculation(newFormData);
  };

  // Call /calculate endpoint with mode: 'zip' to screen properties
  const runCalculation = async (formDataObj) => {
    // Flatten { value: X } structure to flat values
    const extractedFormData = {};
    Object.keys(formDataObj).forEach(zipKey => {
      extractedFormData[zipKey] = {};
      Object.keys(formDataObj[zipKey]).forEach(fieldKey => {
        extractedFormData[zipKey][fieldKey] = formDataObj[zipKey][fieldKey]['value'];
      });
    });

    // Build capex_config with base costs, rates, and tier multipliers
    // Use trueDiy multipliers and rates when budget is selected and trueDiyMode is on
    const effectiveTier = (capexTier === 'budget' && trueDiyMode) ? 'trueDiy' : capexTier;
    const isTrueDiy = effectiveTier === 'trueDiy';
    const capexConfig = {};
    CAPEX_ITEMS.forEach(item => {
      capexConfig[item] = {
        base: CAPEX_BASE_COSTS[item],
        rate: isTrueDiy ? CAPEX_TRUE_DIY_RATES[item] : CAPEX_RATES[item],
        tier_multiplier: CAPEX_TIER_MULTIPLIERS[effectiveTier][item]
      };
    });
    
    // Build capex_ages from condition settings
    const capexAges = {};
    CAPEX_ITEMS.forEach(item => {
      const condition = itemConditions[item] || globalCondition;
      capexAges[item] = Math.round(CAPEX_LIFESPANS[item] * CONDITION_AGE_MULTIPLIERS[condition]);
    });
    
    // Build avg_sqft per ZIP for ratio calculation
    const avgSqftByZip = {};
    Object.keys(marketData).forEach(zip => {
      avgSqftByZip[zip] = marketData[zip]?.avgSqft || 1200;
    });

    // Map lifespans to the keys the calculator expects
    const capexLifespansForBackend = {
      roof: CAPEX_LIFESPANS.roof,
      hvac: CAPEX_LIFESPANS.hvac,
      plumbing: CAPEX_LIFESPANS.plumbing,
      water_heater: CAPEX_LIFESPANS.water_heater,
      appliances: CAPEX_LIFESPANS.appliances,
      flooring: CAPEX_LIFESPANS.flooring,
      'driveway/parking_lot': CAPEX_LIFESPANS.driveway,
      windows: CAPEX_LIFESPANS.windows,
      paint: CAPEX_LIFESPANS.paint,
      'cabinets/counters': CAPEX_LIFESPANS.cabinets,
      structure: CAPEX_LIFESPANS.structure,
      components: CAPEX_LIFESPANS.components,
      landscaping: CAPEX_LIFESPANS.landscaping,
      other: CAPEX_LIFESPANS.other
    };

    try {
      const response = await api.post('/calculate', {
        mode: 'zip',
        form_data: extractedFormData,
        zip_codes: zipCodes,
        property_type: propertyType,
        capex_config: capexConfig,
        capex_ages: capexAges,
        mf_multipliers: CAPEX_MF_MULTIPLIERS,
        avg_sqft: avgSqftByZip,
        capex_lifespans: capexLifespansForBackend
      });
      
      setResults(response.data);
      setExpandedSection('results');
      logger.info('ZIP calculation complete');
    } catch (error) {
      logger.error('Error running ZIP calculation: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  // Calculate days on market from list_date
  const calculateDOM = (listDate) => {
    if (!listDate) return null;
    const listed = new Date(listDate);
    const today = new Date();
    const diffTime = today - listed;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : null;
  };

  // Calculate Price Flexibility Score (PFS) - Seller motivation signals only
  // No baseline - pure urgency indicators (DOM, price drops, distress language)
  const calculatePFS = (prop) => {
    let score = 0; // no baseline - pure motivation signals
    
    // DOM - requires list_date
    const dom = prop.list_date ? calculateDOM(prop.list_date) : null;
    if (dom !== null) {
      if (dom > 120) score += 40;
      else if (dom > 90) score += 30;
      else if (dom > 60) score += 20;
      else if (dom > 30) score += 10;
      else if (dom > 14) score += 5;
    }
    
    // Price drop - can be null
    const priceDrop = prop.price_drop_pct;
    if (priceDrop !== null && priceDrop !== undefined) {
      if (priceDrop > 15) score += 35;
      else if (priceDrop > 10) score += 25;
      else if (priceDrop > 5) score += 15;
      else if (priceDrop > 0) score += 5;
    }
    
    // TLC flag - can be null, true, or false
    if (prop.tlc_flag === true) score += 25;
    
    return Math.min(score, 100);
  };

  // Calculate Deal Quality Score (DQS) - Holistic deal attractiveness
  // Combines CapEx savings, condition, price positioning, and seller motivation
  const calculateDQS = (prop, marketData) => {
    let score = 30; // baseline
    
    const capexKeywords = prop.capex_keywords || [];
    const negativeKeywords = prop.negative_keywords || [];
    const conditionScore = prop.condition_score;
    
    // === CAPEX BONUS (objective - based on actual keywords found) ===
    const capexCount = capexKeywords.length;
    if (capexCount >= 8) score += 20;       // Turnkey - major systems done
    else if (capexCount >= 5) score += 15;  // Well-updated
    else if (capexCount >= 3) score += 10;  // Good updates
    else if (capexCount >= 1) score += 5;   // Some CapEx done
    
    // === CONDITION BONUS (only if no CapEx proof) ===
    if (capexCount === 0 && conditionScore >= 4) score += 3;
    
    // === BELOW-MARKET PRICE/SQFT ===
    if (prop.sqft && prop.list_price && marketData?.avg_price_sqft) {
      const propPsf = parseFloat(prop.list_price) / prop.sqft;
      const marketPsf = marketData.avg_price_sqft;
      const psfRatio = propPsf / marketPsf;
      
      if (psfRatio <= 0.75) score += 15;
      else if (psfRatio <= 0.85) score += 10;
      else if (psfRatio <= 0.95) score += 5;
    }
    
    // === PFS CONTRIBUTION ===
    const pfs = calculatePFS(prop);
    if (pfs >= 70) score += 15;
    else if (pfs >= 50) score += 10;
    else if (pfs >= 30) score += 5;
    
    // === NEGATIVE KEYWORDS PENALTY ===
    if (negativeKeywords.length >= 3) score -= 10;
    else if (negativeKeywords.length >= 1) score -= 5;
    
    return Math.max(0, Math.min(score, 100));
  };

  // Format unit breakdown for MF properties: "2bd/1ba × 2" or "2×2bd/1ba, 1×1bd/1ba"
  const formatUnitBreakdown = (units) => {
    if (!units || units.length === 0) {
      return <span style={{ color: '#e74c3c' }}>No unit data</span>;
    }
    
    // Group units by beds/baths configuration
    const configs = {};
    units.forEach(unit => {
      const key = `${unit.beds || '?'}bd/${unit.baths || '?'}ba`;
      configs[key] = (configs[key] || 0) + 1;
    });
    
    const entries = Object.entries(configs);
    if (entries.length === 1) {
      // All units same config: "2bd/1ba × 2"
      const [config, count] = entries[0];
      return `${config} × ${count}`;
    } else {
      // Mixed configs: "2×2bd/1ba, 1×1bd/1ba"
      return entries.map(([config, count]) => `${count}×${config}`).join(', ');
    }
  };

  // Sorting handler for results table
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted properties
  const getSortedProperties = () => {
    if (!results?.passed_properties) return [];
    
    const sorted = [...results.passed_properties].sort((a, b) => {
      let aVal, bVal;
      
      // DQS is calculated dynamically (replaces PFS as main column)
      if (sortConfig.key === 'dqs') {
        // Get market data for DQS calculation
        const mktDataA = marketData[a.zip_code] || marketData[Object.keys(marketData)[0]];
        const mktDataB = marketData[b.zip_code] || marketData[Object.keys(marketData)[0]];
        aVal = calculateDQS(a, mktDataA);
        bVal = calculateDQS(b, mktDataB);
      } else if (sortConfig.key === 'pfs') {
        aVal = calculatePFS(a);
        bVal = calculatePFS(b);
      } else if (sortConfig.key === 'estimated_rent') {
        // SF estimated rent = rentPerSqft × sqft
        const rentA = marketData[a.zip_code]?.rentPerSqft;
        const rentB = marketData[b.zip_code]?.rentPerSqft;
        aVal = (rentA && a.sqft) ? rentA * a.sqft : null;
        bVal = (rentB && b.sqft) ? rentB * b.sqft : null;
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      
      // Handle null/undefined
      if (aVal == null) aVal = sortConfig.direction === 'asc' ? Infinity : -Infinity;
      if (bVal == null) bVal = sortConfig.direction === 'asc' ? Infinity : -Infinity;
      
      // String comparison for address
      if (sortConfig.key === 'address') {
        return sortConfig.direction === 'asc' 
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }
      
      // Numeric comparison
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  };

  // Generate Zillow search URL
  const getZillowUrl = (address) => {
    const encoded = encodeURIComponent(address);
    return `https://www.zillow.com/homes/${encoded}_rb/`;
  };

  // Toggle property details expansion (financial details)
  const togglePropertyDetails = (propertyId) => {
    if (expandedPropertyId === propertyId) {
      setExpandedPropertyId(null);
    } else {
      setExpandedPropertyId(propertyId);
      setInsightsPropertyId(null); // Close insights when opening details
      setDetailsTab('cashflow'); // Reset to first tab when expanding
      setDetailsViewMode('table'); // Reset to table view when expanding
    }
  };

  // Toggle insights panel expansion
  const toggleInsightsPanel = (propertyId) => {
    if (insightsPropertyId === propertyId) {
      setInsightsPropertyId(null);
    } else {
      setInsightsPropertyId(propertyId);
      setExpandedPropertyId(null); // Close details when opening insights
    }
  };

  // Get DOM freshness indicator
  const getDOMFreshness = (dom) => {
    if (dom === null) return { emoji: '❓', label: 'Unknown' };
    if (dom <= 7) return { emoji: '🟢', label: 'Fresh' };
    if (dom <= 30) return { emoji: '🟡', label: 'Normal' };
    if (dom <= 60) return { emoji: '🟠', label: 'Stale' };
    return { emoji: '🔴', label: 'Very Stale' };
  };

  // Get PFS breakdown for insights panel
  const getPFSBreakdown = (prop) => {
    const breakdown = [];
    const dom = calculateDOM(prop.list_date) || 0;
    const priceDrop = prop.price_drop_pct || 0;
    const tlcFlag = prop.tlc_flag || false;
    const conditionScore = prop.condition_score || 5;
    
    // DOM contribution
    if (dom > 90) breakdown.push({ label: 'DOM 90+', points: 25 });
    else if (dom > 60) breakdown.push({ label: 'DOM 60+', points: 15 });
    else if (dom > 30) breakdown.push({ label: 'DOM 30+', points: 5 });
    
    // Price drop contribution
    if (priceDrop > 10) breakdown.push({ label: 'Price Drop >10%', points: 20 });
    else if (priceDrop > 5) breakdown.push({ label: 'Price Drop >5%', points: 10 });
    
    // TLC contribution
    if (tlcFlag) breakdown.push({ label: 'TLC Flag', points: 15 });
    
    // Condition contribution
    if (conditionScore <= 2) breakdown.push({ label: 'Low Condition', points: 10 });
    else if (conditionScore === 3) breakdown.push({ label: 'Mid Condition', points: 5 });
    
    return breakdown;
  };

  // Render insights panel
  const renderInsightsPanel = (prop) => {
    const dom = calculateDOM(prop.list_date);
    const freshness = getDOMFreshness(dom);
    const pfsScore = calculatePFS(prop);
    const propMarketData = marketData[prop.zip_code] || marketData[Object.keys(marketData)[0]];
    const dqsScore = calculateDQS(prop, propMarketData);
    const originalPrice = prop.price_reduced_amount ? 
      (parseFloat(prop.list_price) + parseFloat(prop.price_reduced_amount)) : null;
    const capexKeywords = prop.capex_keywords || [];
    const negativeKeywords = prop.negative_keywords || [];
    
    // Build DQS breakdown
    const dqsBreakdown = [];
    if (capexKeywords.length >= 8) dqsBreakdown.push({ label: 'CapEx 8+', points: '+20' });
    else if (capexKeywords.length >= 5) dqsBreakdown.push({ label: 'CapEx 5-7', points: '+15' });
    else if (capexKeywords.length >= 3) dqsBreakdown.push({ label: 'CapEx 3-4', points: '+10' });
    else if (capexKeywords.length >= 1) dqsBreakdown.push({ label: 'CapEx 1-2', points: '+5' });
    
    if (capexKeywords.length === 0 && prop.condition_score >= 4) {
      dqsBreakdown.push({ label: 'Condition', points: '+3' });
    }
    
    if (prop.sqft && prop.list_price && propMarketData?.avg_price_sqft) {
      const psfRatio = (parseFloat(prop.list_price) / prop.sqft) / propMarketData.avg_price_sqft;
      if (psfRatio <= 0.75) dqsBreakdown.push({ label: '≤75% market', points: '+15' });
      else if (psfRatio <= 0.85) dqsBreakdown.push({ label: '≤85% market', points: '+10' });
      else if (psfRatio <= 0.95) dqsBreakdown.push({ label: '≤95% market', points: '+5' });
    }
    
    if (pfsScore >= 70) dqsBreakdown.push({ label: 'PFS 70+', points: '+15' });
    else if (pfsScore >= 50) dqsBreakdown.push({ label: 'PFS 50+', points: '+10' });
    else if (pfsScore >= 30) dqsBreakdown.push({ label: 'PFS 30+', points: '+5' });
    
    if (negativeKeywords.length >= 3) dqsBreakdown.push({ label: '3+ negatives', points: '-10' });
    else if (negativeKeywords.length >= 1) dqsBreakdown.push({ label: 'negatives', points: '-5' });
    
    // Build PFS breakdown
    const pfsBreakdown = [];
    if (dom !== null) {
      if (dom > 120) pfsBreakdown.push({ label: 'DOM 120+', points: '+40' });
      else if (dom > 90) pfsBreakdown.push({ label: 'DOM 90+', points: '+30' });
      else if (dom > 60) pfsBreakdown.push({ label: 'DOM 60+', points: '+20' });
      else if (dom > 30) pfsBreakdown.push({ label: 'DOM 30+', points: '+10' });
      else if (dom > 14) pfsBreakdown.push({ label: 'DOM 14+', points: '+5' });
    }
    const priceDrop = prop.price_drop_pct;
    if (priceDrop > 15) pfsBreakdown.push({ label: 'Drop >15%', points: '+35' });
    else if (priceDrop > 10) pfsBreakdown.push({ label: 'Drop >10%', points: '+25' });
    else if (priceDrop > 5) pfsBreakdown.push({ label: 'Drop >5%', points: '+15' });
    else if (priceDrop > 0) pfsBreakdown.push({ label: 'Drop >0%', points: '+5' });
    if (prop.tlc_flag === true) pfsBreakdown.push({ label: 'TLC flag', points: '+25' });
    
    return (
      <tr>
        <td colSpan={8} style={{ padding: 0 }}>
          <InsightsPanel>
            {/* Condition Section */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>Condition</div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                {prop.tlc_flag && <span title="Needs TLC" style={{ marginRight: '6px' }}>⚠️</span>}
                {prop.condition_score ? `${prop.condition_score}/5` : 'Not assessed'}
              </div>
            </div>
            
            {/* DOM Section */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>Days on Market</div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                {freshness.emoji} {dom !== null ? `${dom} days` : 'Unknown'} 
                <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '6px' }}>({freshness.label})</span>
              </div>
            </div>
            
            {/* Price History Section */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>Price History</div>
              <div style={{ fontSize: '1rem', fontWeight: 600 }}>
                {originalPrice ? (
                  <>
                    {formatCurrency(originalPrice)} → {formatCurrency(prop.list_price)}
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>🔻{prop.price_drop_pct}%</span>
                  </>
                ) : (
                  <span style={{ color: '#888' }}>No price changes detected</span>
                )}
              </div>
            </div>
            
            {/* CapEx Keywords */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>CapEx Done ({capexKeywords.length})</div>
              <div style={{ fontSize: '0.85rem' }}>
                {capexKeywords.length > 0 ? (
                  capexKeywords.map((kw, i) => (
                    <span key={i} style={{ 
                      background: '#3498db20', 
                      color: '#3498db', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      marginRight: '4px',
                      marginBottom: '4px',
                      display: 'inline-block',
                      fontSize: '0.75rem'
                    }}>{kw}</span>
                  ))
                ) : (
                  <span style={{ color: '#888' }}>None detected</span>
                )}
              </div>
            </div>
            
            {/* Positive Keywords */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>Condition Keywords</div>
              <div style={{ fontSize: '0.85rem' }}>
                {prop.positive_keywords && prop.positive_keywords.length > 0 ? (
                  prop.positive_keywords.map((kw, i) => (
                    <span key={i} style={{ 
                      background: '#27ae6020', 
                      color: '#27ae60', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      marginRight: '4px',
                      marginBottom: '4px',
                      display: 'inline-block',
                      fontSize: '0.75rem'
                    }}>{kw}</span>
                  ))
                ) : (
                  <span style={{ color: '#888' }}>None detected</span>
                )}
              </div>
            </div>
            
            {/* Negative Keywords */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>Red Flags</div>
              <div style={{ fontSize: '0.85rem' }}>
                {negativeKeywords.length > 0 ? (
                  negativeKeywords.map((kw, i) => (
                    <span key={i} style={{ 
                      background: '#e74c3c20', 
                      color: '#e74c3c', 
                      padding: '2px 8px', 
                      borderRadius: '12px',
                      marginRight: '4px',
                      marginBottom: '4px',
                      display: 'inline-block',
                      fontSize: '0.75rem'
                    }}>{kw}</span>
                  ))
                ) : (
                  <span style={{ color: '#888' }}>None detected</span>
                )}
              </div>
            </div>
            
            {/* DQS Breakdown */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>DQS Breakdown</div>
              <div style={{ fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Base 30</span>
                {dqsBreakdown.map((item, i) => (
                  <span key={i} style={{ color: item.points.startsWith('-') ? '#e74c3c' : '#27ae60' }}> {item.points.startsWith('-') ? '' : '+'}{item.label} ({item.points})</span>
                ))}
                <span style={{ 
                  fontWeight: 700, 
                  marginLeft: '8px',
                  color: dqsScore >= 70 ? '#27ae60' : dqsScore >= 50 ? '#f39c12' : dqsScore >= 30 ? '#e67e22' : '#e74c3c'
                }}>= {dqsScore}</span>
              </div>
            </div>
            
            {/* PFS Breakdown */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '4px', textTransform: 'uppercase' }}>PFS (Seller Motivation)</div>
              <div style={{ fontSize: '0.85rem' }}>
                {pfsBreakdown.length > 0 ? (
                  <>
                    {pfsBreakdown.map((item, i) => (
                      <span key={i} style={{ color: '#f39c12' }}>{i > 0 ? ' + ' : ''}{item.label} ({item.points})</span>
                    ))}
                    <span style={{ 
                      fontWeight: 700, 
                      marginLeft: '8px',
                      color: pfsScore >= 70 ? '#27ae60' : pfsScore >= 50 ? '#f39c12' : pfsScore >= 30 ? '#e67e22' : '#888'
                    }}>= {pfsScore}</span>
                  </>
                ) : (
                  <span style={{ color: '#888' }}>No motivation signals (firm seller)</span>
                )}
              </div>
            </div>
          </InsightsPanel>
        </td>
      </tr>
    );
  };

  // Render chart for property details
  const renderDetailsChart = (prop) => {
    switch (detailsTab) {
      case 'cashflow':
        if (!prop.cash_flow || prop.cash_flow.length === 0) return <p>No cash flow data available.</p>;
        return (
          <DetailsChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={prop.cash_flow} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Cash Flow']}
                  contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                  labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                  itemStyle={{ color: '#333' }}
                />
                <ReferenceLine y={0} stroke={theme.colors.textSecondary} />
                <Bar dataKey="Cash Flow" fill={theme.colors.successColor} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </DetailsChartContainer>
        );
      
      case 'amortization':
        if (!prop.amortization || prop.amortization.length === 0) return <p>No amortization data available.</p>;
        return (
          <DetailsChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={prop.amortization} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                  contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                  labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                  itemStyle={{ color: '#333' }}
                />
                <Bar dataKey="Principal Payment" stackId="payment" fill={theme.colors.successColor} radius={[0, 0, 0, 0]} name="Principal" />
                <Bar dataKey="Interest Payment" stackId="payment" fill={theme.colors.errorColor} radius={[4, 4, 0, 0]} name="Interest" />
              </BarChart>
            </ResponsiveContainer>
          </DetailsChartContainer>
        );
      
      case 'irr':
        if (!prop.annual_irrs || prop.annual_irrs.length === 0) return <p>No IRR data available.</p>;
        return (
          <DetailsChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prop.annual_irrs} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `${v}%`} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'IRR']}
                  contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                  labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                  itemStyle={{ color: '#333' }}
                />
                <ReferenceLine y={0} stroke={theme.colors.errorColor} strokeDasharray="3 3" />
                <ReferenceLine y={10} stroke={theme.colors.successColor} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="IRR" stroke={theme.colors.accent} strokeWidth={2} dot={{ fill: theme.colors.accent }} />
              </LineChart>
            </ResponsiveContainer>
          </DetailsChartContainer>
        );
      
      case 'coc':
        if (!prop.coc || prop.coc.length === 0) return <p>No COC data available.</p>;
        return (
          <DetailsChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prop.coc} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderColor} />
                <XAxis dataKey="Year" stroke={theme.colors.textSecondary} />
                <YAxis stroke={theme.colors.textSecondary} tickFormatter={(v) => `${v}%`} />
                <Tooltip 
                  formatter={(value) => [value !== null ? `${value}%` : 'N/A', 'COC']}
                  contentStyle={{ backgroundColor: '#ffffff', border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', padding: '10px 14px' }}
                  labelStyle={{ color: '#333', fontWeight: 600, marginBottom: '4px' }}
                  itemStyle={{ color: '#333' }}
                />
                <ReferenceLine y={0} stroke={theme.colors.errorColor} strokeDasharray="3 3" />
                <ReferenceLine y={8} stroke={theme.colors.successColor} strokeDasharray="3 3" />
                <Line type="monotone" dataKey="COC" stroke={theme.colors.warningColor} strokeWidth={2} dot={{ fill: theme.colors.warningColor }} />
              </LineChart>
            </ResponsiveContainer>
          </DetailsChartContainer>
        );
      
      case 'valuation':
        // Valuation works better as table
        return null;
      
      default:
        return null;
    }
  };

  // Handle export to Excel
  const handleExport = () => {
    if (!results?.passed_properties) return;
    
    const screening = {
      requiredCOC,
      cocTerm,
      holdPeriod
    };
    
    exportZipModeResults(
      results.passed_properties,
      params,
      screening,
      exportIncludeDetails,
      exportIncludeInputs,
      propertyType
    );
    
    setShowExportDropdown(false);
    setExportIncludeDetails(false);
    setExportIncludeInputs(false);
  };

  // Render property details tables
  const renderPropertyDetails = (prop) => {
    if (!prop) return null;
    
    const colCount = 8; // Both SF and MF now have 8 columns
    
    return (
      <DetailsRow>
        <td colSpan={colCount}>
          <DetailsContent>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <DetailsTabs style={{ marginBottom: 0 }}>
                <DetailsTab $active={detailsTab === 'cashflow'} onClick={() => setDetailsTab('cashflow')}>Cash Flow</DetailsTab>
                <DetailsTab $active={detailsTab === 'amortization'} onClick={() => setDetailsTab('amortization')}>Amortization</DetailsTab>
                <DetailsTab $active={detailsTab === 'irr'} onClick={() => setDetailsTab('irr')}>IRR</DetailsTab>
                <DetailsTab $active={detailsTab === 'coc'} onClick={() => setDetailsTab('coc')}>COC</DetailsTab>
                <DetailsTab $active={detailsTab === 'valuation'} onClick={() => setDetailsTab('valuation')}>Valuation</DetailsTab>
              </DetailsTabs>
              {detailsTab !== 'valuation' && (
                <DetailsViewToggle>
                  <DetailsViewButton 
                    $active={detailsViewMode === 'table'} 
                    onClick={() => setDetailsViewMode('table')}
                    title="Table View"
                  >
                    <IoGridOutline />
                  </DetailsViewButton>
                  <DetailsViewButton 
                    $active={detailsViewMode === 'chart'} 
                    onClick={() => setDetailsViewMode('chart')}
                    title="Chart View"
                  >
                    <IoBarChartOutline />
                  </DetailsViewButton>
                </DetailsViewToggle>
              )}
            </div>
            
            {detailsViewMode === 'chart' && detailsTab !== 'valuation' ? (
              renderDetailsChart(prop)
            ) : (
            <DetailsTableWrapper>
              {detailsTab === 'cashflow' && prop.cash_flow && (
                <DetailsTable>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Gross Rev</th>
                      <th>Vacancy</th>
                      <th>EGR</th>
                      <th>Expenses</th>
                      <th>NOI</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Cash Flow</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prop.cash_flow.map((row, i) => (
                      <tr key={i}>
                        <td>{row.Year}</td>
                        <td>{formatCurrency(row["Potential Gross Revenue"])}</td>
                        <td style={{ color: '#dc3545' }}>-{formatCurrency(row["Vacancy Loss"])}</td>
                        <td>{formatCurrency(row["Effective Gross Revenue"])}</td>
                        <td style={{ color: '#dc3545' }}>-{formatCurrency(row["Operating Expenses"])}</td>
                        <td>{formatCurrency(row["NOI"])}</td>
                        <td style={{ color: '#dc3545' }}>-{formatCurrency(row["Principal Payment"])}</td>
                        <td style={{ color: '#dc3545' }}>-{formatCurrency(row["Interest Payment"])}</td>
                        <td style={{ color: row["Cash Flow"] >= 0 ? '#28a745' : '#dc3545', fontWeight: 600 }}>{formatCurrency(row["Cash Flow"])}</td>
                      </tr>
                    ))}
                  </tbody>
                </DetailsTable>
              )}
              
              {detailsTab === 'amortization' && prop.amortization && (
                <DetailsTable>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Principal</th>
                      <th>Interest</th>
                      <th>Total Payment</th>
                      <th>Remaining Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prop.amortization.map((row, i) => (
                      <tr key={i}>
                        <td>{row.Year}</td>
                        <td>{formatCurrency(row["Principal Payment"])}</td>
                        <td>{formatCurrency(row["Interest Payment"])}</td>
                        <td>{formatCurrency(row["Principal Payment"] + row["Interest Payment"])}</td>
                        <td>{formatCurrency(row["Remaining Balance"])}</td>
                      </tr>
                    ))}
                  </tbody>
                </DetailsTable>
              )}
              
              {detailsTab === 'irr' && prop.annual_irrs && (
                <DetailsTable>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>IRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prop.annual_irrs.map((row, i) => (
                      <tr key={i}>
                        <td>{row.Year}</td>
                        <td style={{ color: row.IRR >= 0 ? '#28a745' : '#dc3545', fontWeight: 600 }}>
                          {row.IRR !== null ? `${row.IRR}%` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DetailsTable>
              )}
              
              {detailsTab === 'coc' && prop.coc && (
                <DetailsTable>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>COC %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prop.coc.map((row, i) => (
                      <tr key={i}>
                        <td>{row.Year}</td>
                        <td style={{ color: row.COC >= 0 ? '#28a745' : '#dc3545', fontWeight: 600 }}>
                          {row.COC !== null ? `${row.COC}%` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DetailsTable>
              )}
              
              {detailsTab === 'valuation' && prop.valuation && (
                <DetailsTable>
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>NOI</th>
                      <th>Sale Price</th>
                      <th>Loan Balance</th>
                      <th>Equity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prop.valuation.map((row, i) => (
                      <tr key={i}>
                        <td>{row.Year}</td>
                        <td>{formatCurrency(row["NOI"])}</td>
                        <td>{formatCurrency(row["Sale Price"])}</td>
                        <td>{formatCurrency(row["Remaining Balance"])}</td>
                        <td style={{ color: row["Equity"] >= 0 ? '#28a745' : '#dc3545', fontWeight: 600 }}>{formatCurrency(row["Equity"])}</td>
                      </tr>
                    ))}
                  </tbody>
                </DetailsTable>
              )}
            </DetailsTableWrapper>
            )}
          </DetailsContent>
        </td>
      </DetailsRow>
    );
  };

  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={onBack}>← Back to Mode Selection</BackButton>
        <PageTitle>ZIP Code Market Screening</PageTitle>
      </PageHeader>

      {/* Section 1: Markets */}
      <AccordionSection
        stepNumber={1}
        title="Markets"
        summary={expandedSection !== 1 ? getMarketsSummary() : null}
        isExpanded={expandedSection === 1}
        isCompleted={zipCodes.length > 0}
        onToggle={() => setExpandedSection(expandedSection === 1 ? null : 1)}
      >
        <ZipContainer>
          <ZipInputRow>
            <ZipInput
              id="zip-code-input"
              name="zip-code-input"
              aria-label="Enter 5-digit ZIP code"
              type="text"
              placeholder="Enter 5-digit ZIP"
              value={newZip}
              onChange={(e) => setNewZip(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddZip()}
              maxLength={5}
              autoComplete="off"
            />
            <AddButton onClick={handleAddZip}>
              <IoAdd size={18} />
              Add ZIP
            </AddButton>
          </ZipInputRow>

          {zipsLoading ? (
            <NoZipsMessage>Loading saved ZIP codes...</NoZipsMessage>
          ) : zipCodes.length > 0 ? (
            <ZipChips>
              {zipCodes.map((zip) => (
                <ZipChip key={zip}>
                  {zip}
                  <RemoveChipButton onClick={() => handleRemoveZip(zip)}>
                    <IoClose size={12} />
                  </RemoveChipButton>
                </ZipChip>
              ))}
            </ZipChips>
          ) : (
            <NoZipsMessage>Add ZIP codes to screen markets for investment opportunities.</NoZipsMessage>
          )}

        </ZipContainer>
      </AccordionSection>

      {/* Section 2: Property Type */}
      <AccordionSection
        stepNumber={2}
        title="Property Type"
        summary={expandedSection !== 'propertyType' ? (propertyType === 'single_family' ? 'Single Family' : 'Multi-Family') : null}
        isExpanded={expandedSection === 'propertyType'}
        isCompleted={true}
        onToggle={() => setExpandedSection(expandedSection === 'propertyType' ? null : 'propertyType')}
      >
        <PropertyTypeContainer>
          <PropertyTypeDescription>
            Select the type of properties you want to analyze. This determines which market data and listings we fetch.
          </PropertyTypeDescription>
          <PropertyTypeOptions>
            <PropertyTypeCard 
              $active={propertyType === 'single_family'}
              onClick={() => {
                if (propertyType !== 'single_family') {
                  setPropertyType('single_family');
                  setMarketData({});
                  setEditableMarketData({});
                  setFetchedMarketData({});
                  setResults(null);
                }
              }}
            >
              <PropertyTypeIcon>🏠</PropertyTypeIcon>
              <PropertyTypeLabel $active={propertyType === 'single_family'}>Single Family</PropertyTypeLabel>
              <PropertyTypeSubtext>Houses, townhomes, condos</PropertyTypeSubtext>
            </PropertyTypeCard>
            <PropertyTypeCard 
              $active={propertyType === 'multi_family'}
              onClick={() => {
                if (propertyType !== 'multi_family') {
                  setPropertyType('multi_family');
                  setMarketData({});
                  setEditableMarketData({});
                  setFetchedMarketData({});
                  setResults(null);
                }
              }}
            >
              <PropertyTypeIcon>🏢</PropertyTypeIcon>
              <PropertyTypeLabel $active={propertyType === 'multi_family'}>Multi-Family</PropertyTypeLabel>
              <PropertyTypeSubtext>Duplexes, triplexes, fourplexes</PropertyTypeSubtext>
            </PropertyTypeCard>
          </PropertyTypeOptions>
        </PropertyTypeContainer>
      </AccordionSection>

      {/* Quirky loading messages while fetching data */}
      {(isLoading || loadingError) && (
        <QuirkyLoadingMessage 
          error={loadingError}
          showProgress={showProgress}
          progress={progress}
          statusText={progressStatus}
        />
      )}

      {/* Section 3: Market Data for Your ZIPs */}
      <AccordionSection
        stepNumber={3}
        title="Market Data for Your ZIPs"
        summary={expandedSection !== 'marketData' ? getMarketDataSummary() : null}
        isExpanded={expandedSection === 'marketData'}
        isCompleted={Object.keys(editableMarketData).length > 0}
        onToggle={() => setExpandedSection(expandedSection === 'marketData' ? null : 'marketData')}
      >
        <MarketDataSection>
          <MarketDataDescription>
            Below are the market values we found for your ZIP codes. You can edit any value before running the analysis.
            The gray text shows the original fetched value for reference.
            <UtilitiesTip>Set utilities to $0 if tenants pay utilities!</UtilitiesTip>
          </MarketDataDescription>
          
          {Object.keys(editableMarketData).length > 0 && (
            <ResetButtonContainer>
              <ResetButton onClick={resetMarketDataToFetched}>
                Reset to Fetched Values
              </ResetButton>
            </ResetButtonContainer>
          )}
          
          {Object.keys(editableMarketData).length > 0 ? (
            <EditableTable>
              <EditableTableHead>
                <tr>
                  <th>ZIP Code</th>
                  <th>Rent/Sqft <FieldTooltip light text="Average rent per square foot in this market. Used to estimate monthly rent: Rent/Sqft × Property Sqft." /></th>
                  <th>Tax Rate % <FieldTooltip light text="Annual property tax rate as a percentage of property value. Varies significantly by location." /></th>
                  <th>Insurance $/yr <FieldTooltip light text="Annual homeowners/landlord insurance cost. Based on market averages but can vary by property." /></th>
                  <th>Utilities $/yr <FieldTooltip light text="Annual utility costs if landlord-paid. Set to 0 if tenants pay all utilities." /></th>
                  <th>Tax Increase % <FieldTooltip light text="Expected annual property tax increase. Typically 1-3% depending on local reassessment policies." /></th>
                  <th>Cap Rate % <FieldTooltip light text="Capitalization rate for this market. Used for property valuation: Value = NOI ÷ Cap Rate. Lower cap rate = higher values." /></th>
                </tr>
              </EditableTableHead>
              <EditableTableBody>
                {Object.entries(editableMarketData).map(([zip, data]) => (
                  <tr key={zip}>
                    <td>{zip}</td>
                    <td>
                      <FetchedValue $isInvalid={!fetchedMarketData[zip]?.rentPerSqft || fetchedMarketData[zip]?.rentPerSqft <= 0}>
                        Fetched: {fetchedMarketData[zip]?.rentPerSqft > 0 ? `$${fetchedMarketData[zip].rentPerSqft.toFixed(2)}` : 'No Data'}
                        {(!fetchedMarketData[zip]?.rentPerSqft || fetchedMarketData[zip]?.rentPerSqft <= 0) && ' ⚠️'}
                      </FetchedValue>
                      <EditableInput
                        id={`market-${zip}-rentPerSqft`}
                        name={`market-${zip}-rentPerSqft`}
                        aria-label={`Rent per sqft for ZIP ${zip}`}
                        type="number"
                        step="0.01"
                        value={data.rentPerSqft ?? ''}
                        onChange={(e) => handleEditableDataChange(zip, 'rentPerSqft', e.target.value)}
                        onFocus={handleFieldFocus}
                        autoComplete="off"
                      />
                    </td>
                    <td>
                      <FetchedValue>Fetched: {fetchedMarketData[zip]?.taxRate?.toFixed(2) || '-'}%</FetchedValue>
                      <EditableInput
                        id={`market-${zip}-taxRate`}
                        name={`market-${zip}-taxRate`}
                        aria-label={`Tax rate for ZIP ${zip}`}
                        type="number"
                        step="0.1"
                        value={data.taxRate ?? ''}
                        onChange={(e) => handleEditableDataChange(zip, 'taxRate', e.target.value)}
                        onFocus={handleFieldFocus}
                        autoComplete="off"
                      />
                    </td>
                    <td>
                      <FetchedValue>Fetched: ${fetchedMarketData[zip]?.insurance?.toLocaleString() || '-'}</FetchedValue>
                      <EditableInput
                        id={`market-${zip}-insurance`}
                        name={`market-${zip}-insurance`}
                        aria-label={`Insurance for ZIP ${zip}`}
                        type="number"
                        step="100"
                        value={data.insurance ?? ''}
                        onChange={(e) => handleEditableDataChange(zip, 'insurance', e.target.value)}
                        onFocus={handleFieldFocus}
                        autoComplete="off"
                      />
                    </td>
                    <td>
                      <FetchedValue>Fetched: ${fetchedMarketData[zip]?.utilities?.toLocaleString() || '-'}</FetchedValue>
                      <EditableInput
                        id={`market-${zip}-utilities`}
                        name={`market-${zip}-utilities`}
                        aria-label={`Utilities for ZIP ${zip}`}
                        type="number"
                        step="100"
                        value={data.utilities ?? ''}
                        onChange={(e) => handleEditableDataChange(zip, 'utilities', e.target.value)}
                        onFocus={handleFieldFocus}
                        autoComplete="off"
                      />
                    </td>
                    <td>
                      <FetchedValue>Fetched: {fetchedMarketData[zip]?.taxIncrease?.toFixed(1) || '-'}%</FetchedValue>
                      <EditableInput
                        id={`market-${zip}-taxIncrease`}
                        name={`market-${zip}-taxIncrease`}
                        aria-label={`Tax increase for ZIP ${zip}`}
                        type="number"
                        step="0.1"
                        value={data.taxIncrease ?? ''}
                        onChange={(e) => handleEditableDataChange(zip, 'taxIncrease', e.target.value)}
                        onFocus={handleFieldFocus}
                        autoComplete="off"
                      />
                    </td>
                    <td>
                      <FetchedValue $isInvalid={!fetchedMarketData[zip]?.capRate || fetchedMarketData[zip]?.capRate <= 0}>
                        Fetched: {fetchedMarketData[zip]?.capRate > 0 ? `${fetchedMarketData[zip].capRate.toFixed(1)}%` : 'No Data'}
                        {(!fetchedMarketData[zip]?.capRate || fetchedMarketData[zip]?.capRate <= 0) && ' ⚠️'}
                      </FetchedValue>
                      <EditableInput
                        id={`market-${zip}-capRate`}
                        name={`market-${zip}-capRate`}
                        aria-label={`Cap rate for ZIP ${zip}`}
                        type="number"
                        step="0.1"
                        value={data.capRate ?? ''}
                        onChange={(e) => handleEditableDataChange(zip, 'capRate', e.target.value)}
                        onFocus={handleFieldFocus}
                        autoComplete="off"
                      />
                    </td>
                  </tr>
                ))}
              </EditableTableBody>
            </EditableTable>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              Add ZIP codes in Step 1 and click "Fetch Market Data" to load values.
            </p>
          )}
        </MarketDataSection>
      </AccordionSection>

      {/* Section 4: Parameters */}
      <AccordionSection
        stepNumber={4}
        title="Analysis Parameters"
        summary={expandedSection !== 'parameters' ? getParametersSummary() : null}
        isExpanded={expandedSection === 'parameters'}
        isCompleted={true}
        onToggle={() => setExpandedSection(expandedSection === 'parameters' ? null : 'parameters')}
      >
        {/* Remember Values Toggle */}
        <RememberToggleContainer>
          <RememberToggleLabel>
            <RememberToggleCheckbox
              id="remember-values-checkbox"
              name="remember-values-checkbox"
              type="checkbox"
              checked={rememberValues}
              onChange={(e) => handleRememberToggle(e.target.checked)}
            />
            <IoSaveOutline size={16} />
            <label htmlFor="remember-values-checkbox">Remember my values</label>
          </RememberToggleLabel>
          <RememberToggleHint>
            {rememberValues ? 'Values will persist on refresh' : 'Remember your analysis inputs throughout this session'}
          </RememberToggleHint>
        </RememberToggleContainer>

        <InputGroup title="Loan Terms">
          <FormField
            label="Loan Amount"
            suffix="%"
            value={params.loanAmount}
            onChange={(e) => handleParamChange('loanAmount', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Loan-to-value ratio. A 75% loan means 25% down payment. Higher LTV = less cash needed upfront but higher monthly payments."
          />
          <FormField
            label="Interest Rate"
            suffix="%"
            value={params.interestRate}
            onChange={(e) => handleParamChange('interestRate', e.target.value)}
            onFocus={handleFieldFocus}
            step={0.125}
            tooltip="Annual interest rate on the mortgage. This significantly impacts your monthly payment and cash flow."
          />
          <FormField
            label="Loan Term"
            suffix="yrs"
            value={params.loanTerm}
            onChange={(e) => handleParamChange('loanTerm', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="How long until the loan must be paid off or refinanced. Typically 30 years for residential, 5-10 years for commercial."
          />
          <FormField
            label="Amortization"
            suffix="yrs"
            value={params.amortizationPeriod}
            onChange={(e) => handleParamChange('amortizationPeriod', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Period over which principal is paid down. Longer amortization = lower payments but more interest paid overall."
          />
        </InputGroup>

        <InputGroup title="Operating Expenses">
          <FormField
            label="Vacancy"
            suffix="%"
            value={params.vacancy}
            onChange={(e) => handleParamChange('vacancy', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Expected annual vacancy rate. 5% is typical for good markets. Accounts for turnover, repairs between tenants, and unleased periods."
          />
          <FormField
            label="Management"
            suffix="%"
            value={params.managementFee}
            onChange={(e) => handleParamChange('managementFee', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Property management fee as a percentage of gross rent. Typically 8-10% for residential. Even if self-managing, include this for accurate analysis."
          />
          <FormField
            label="Insurance"
            prefix="$"
            suffix="/yr"
            value={params.insurance}
            onChange={(e) => handleParamChange('insurance', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Annual homeowners/landlord insurance premium. Fetched from market data but can be adjusted based on property specifics."
          />
          <FormField
            label="Other Expenses"
            prefix="$"
            suffix="/mo"
            value={params.otherExpenses}
            onChange={(e) => handleParamChange('otherExpenses', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Additional monthly expenses not covered elsewhere: HOA fees, lawn care, pest control, misc repairs, etc."
          />
        </InputGroup>

        <InputGroup title="CapEx Reserves">
          <CapExExplanation>
            <p>
              <strong>Capital Expenditures (CapEx)</strong> are major repairs and replacements that occur during property ownership—roof, HVAC, plumbing, etc. Unlike regular maintenance, these are large, infrequent expenses that can significantly impact your cash flow if you're not prepared.
            </p>
            <p>
              <strong>How it works:</strong> For each property, we calculate replacement costs using the formula:
            </p>
            <ul>
              <li><strong>Per-Property Cost</strong> = (Base + Sqft × Rate) × Tier Multiplier</li>
              <li><strong>Annual Reserve</strong> = Replacement Cost ÷ (Lifespan − Current Age)</li>
              {propertyType === 'multi_family' && <li><strong>Multi-Family</strong>: Per-unit items (HVAC, water heaters, appliances, cabinets) use scaled multipliers that account for bulk/labor savings when replacing multiple units</li>}
            </ul>
            <p>
              <strong>Condition</strong> estimates how old each component is as a percentage of its lifespan:
            </p>
            <ul>
              <li><strong>Outdated (90%)</strong> — Components near end of life, need replacement soon. Higher annual reserves.</li>
              <li><strong>Current (50%)</strong> — Mid-life, typical for older properties. Moderate reserves.</li>
              <li><strong>New (10%)</strong> — Recently renovated or new construction. Lower reserves since replacement is far off.</li>
            </ul>
            <p>
              <strong>Tier</strong> affects labor costs. <strong>Budget</strong> assumes DIY or budget contractors (40-95% of Standard). <strong>Standard</strong> is licensed contractors (baseline). <strong>Premium</strong> is high-end specialists (120-160% of Standard).
            </p>
            <p>
              <strong>True DIY</strong> (checkbox under Budget) is for licensed contractors or those doing ALL work themselves—including HVAC, plumbing, and electrical. It eliminates labor costs entirely on labor-heavy items, showing equipment/materials only. Only select this if you can legally perform licensed trade work.
            </p>
            <p>
              <em>Tip: If very few properties pass screening, try Budget tier or New condition. The fewer years remaining until replacement, the higher the annual reserve.</em>
            </p>
          </CapExExplanation>
          
          <TierSelectorContainer>
            <TierSelectorLabel>Estimate Level:</TierSelectorLabel>
            <TierButtonGroup>
              <TierButton $active={capexTier === 'budget'} onClick={() => setCapexTier('budget')}>
                Budget
              </TierButton>
              <TierButton $active={capexTier === 'standard'} onClick={() => setCapexTier('standard')}>
                Standard
              </TierButton>
              <TierButton $active={capexTier === 'premium'} onClick={() => setCapexTier('premium')}>
                Premium
              </TierButton>
            </TierButtonGroup>
            <TierDescription>
              {capexTier === 'budget' && 'DIY or budget contractors'}
              {capexTier === 'standard' && 'Licensed general contractors'}
              {capexTier === 'premium' && 'High-end or specialized contractors'}
            </TierDescription>
            {capexTier === 'budget' && (
              <TrueDiyToggle>
                <input
                  type="checkbox"
                  id="trueDiyToggle"
                  name="trueDiyToggle"
                  checked={trueDiyMode}
                  onChange={(e) => setTrueDiyMode(e.target.checked)}
                />
                <label htmlFor="trueDiyToggle">
                  True DIY (I'm licensed or doing all work myself)
                </label>
              </TrueDiyToggle>
            )}
          </TierSelectorContainer>
          
          <ConditionSelectorContainer>
            <TierSelectorLabel>Property Condition:</TierSelectorLabel>
            <ConditionButtonGroup>
              <ConditionButton $active={globalCondition === 'outdated'} onClick={() => setGlobalCondition('outdated')}>
                Outdated (90%)
              </ConditionButton>
              <ConditionButton $active={globalCondition === 'current'} onClick={() => setGlobalCondition('current')}>
                Current (50%)
              </ConditionButton>
              <ConditionButton $active={globalCondition === 'new'} onClick={() => setGlobalCondition('new')}>
                New (10%)
              </ConditionButton>
            </ConditionButtonGroup>
          </ConditionSelectorContainer>
          
          <div style={{ marginTop: '16px', width: '100%' }}>
            {CAPEX_ITEMS.map(item => (
              <CapExItemRow key={item}>
                <CapExItemLabel id={`capex-label-${item}`}>{CAPEX_LABELS[item]}</CapExItemLabel>
                <CapExRateDisplay>
                  {CAPEX_RATES[item] > 0 ? `$${CAPEX_RATES[item].toFixed(2)}/sqft` : ''}
                </CapExRateDisplay>
                <CapExItemInput
                  id={`capex-cost-${item}`}
                  name={`capex-cost-${item}`}
                  type="number"
                  value={capexCosts[item] || 0}
                  onChange={(e) => handleCapexCostChange(item, e.target.value)}
                  onFocus={handleFieldFocus}
                  placeholder="$0"
                  autoComplete="off"
                  aria-labelledby={`capex-label-${item}`}
                />
                <CapExConditionSelect
                  id={`capex-condition-${item}`}
                  name={`capex-condition-${item}`}
                  value={getItemCondition(item)}
                  onChange={(e) => handleItemConditionChange(item, e.target.value)}
                  aria-labelledby={`capex-label-${item}`}
                >
                  <option value="outdated">Outdated</option>
                  <option value="current">Current</option>
                  <option value="new">New</option>
                </CapExConditionSelect>
              </CapExItemRow>
            ))}
          </div>
        </InputGroup>

        <InputGroup title="Market Assumptions">
          <FormField
            label="Annual Rent Increase"
            suffix="%"
            value={params.rentIncrease}
            onChange={(e) => handleParamChange('rentIncrease', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Expected annual rent growth. 2-3% is typical for stable markets. This compounds yearly, improving your cash flow over time."
          />
          <FormField
            label="Annual Expense Increase"
            suffix="%"
            value={params.expenseIncrease}
            onChange={(e) => handleParamChange('expenseIncrease', e.target.value)}
            onFocus={handleFieldFocus}
            tooltip="Expected annual increase in operating expenses (insurance, repairs, etc.). Typically 2-3% tracking inflation."
          />
        </InputGroup>
      </AccordionSection>

      {/* Section 5: Screening Threshold */}
      <AccordionSection
        stepNumber={5}
        title="Screening Threshold"
        summary={expandedSection !== 'screening' ? getScreeningSummary() : null}
        isExpanded={expandedSection === 'screening'}
        isCompleted={true}
        onToggle={() => setExpandedSection(expandedSection === 'screening' ? null : 'screening')}
      >
        <InputGroup title="Cash-on-Cash Requirements">
          <FormField
            label="Required COC Return"
            suffix="%"
            value={requiredCOC}
            onChange={(e) => setRequiredCOC(parseFloat(e.target.value) || 0)}
            onFocus={handleFieldFocus}
            tooltip="Minimum cash-on-cash return to filter properties. COC = (Annual Cash Flow ÷ Total Cash Invested) × 100. 8-12% is a common target."
          />
          <FormField
            label="COC Target Year"
            suffix="years"
            value={cocTerm}
            onChange={(e) => setCocTerm(parseFloat(e.target.value) || 0)}
            onFocus={handleFieldFocus}
            tooltip="Year at which to evaluate COC for screening. Properties must meet the Required COC % by this year to pass."
          />
          <FormField
            label="Hold Period"
            suffix="years"
            value={holdPeriod}
            onChange={(e) => {
              const val = parseFloat(e.target.value) || 1;
              // Hold period must be >= COC target year
              setHoldPeriod(Math.max(val, cocTerm));
            }}
            onFocus={handleFieldFocus}
            tooltip="Number of years you plan to hold the property. Controls how many years of data are shown in the details tables. Must be ≥ COC Target Year."
          />
        </InputGroup>
      </AccordionSection>

      {/* Action Button - Changes based on current section */}
      {/* Hide button when results exist and we're viewing results or no section is expanded */}
      {!(results && (expandedSection === 'results' || expandedSection === null)) && (
        <RunAnalysisButton 
          data-run-analysis
          onClick={() => {
            if (expandedSection === 1) {
              // Move to Property Type selection (Step 2)
              setExpandedSection('propertyType');
            } else if (expandedSection === 'propertyType') {
              // Fetch market data for the ZIP codes with selected property type
              runAnalysis();
            } else if (expandedSection === 'marketData') {
              // Move to parameters
              setExpandedSection('parameters');
            } else if (expandedSection === 'parameters') {
              // Move to screening
              setExpandedSection('screening');
            } else if (expandedSection === 'screening') {
              // Run calculation with edited data
              runCalculationWithData();
            } else {
              // Advance to next section
              const nextSection = expandedSection === null ? 1 : expandedSection + 1;
              setExpandedSection(nextSection);
            }
          }}
          disabled={
            (expandedSection === 1 && zipCodes.length === 0) ||
            (expandedSection === 'screening' && (Object.keys(editableMarketData).length === 0 || isLoading)) ||
            isLoading
          }
        >
          {isLoading ? 'Loading...' : (
            expandedSection === 1 ? (
              <>
                Next Step
                <IoArrowForward size={20} />
              </>
            ) : expandedSection === 'propertyType' ? (
              <>
                <IoSearch size={20} />
                Fetch Market Data
              </>
            ) : expandedSection === 'screening' ? (
              <>
                <IoSearch size={20} />
                {results ? 'Update Analysis' : 'Run Analysis'}
              </>
            ) : (
              <>
                Next Step
                <IoArrowForward size={20} />
              </>
            )
          )}
        </RunAnalysisButton>
      )}

      {/* Results Section */}
      {results && (
        <AccordionSection
          stepNumber="✓"
          title="Results: Passed Listings"
          isExpanded={expandedSection === 'results'}
          isCompleted={true}
          onToggle={() => setExpandedSection(expandedSection === 'results' ? null : 'results')}
        >
          <ResultsContainer>
            {results.warnings && results.warnings.length > 0 && (
              <WarningBanner>
                <span>⚠️</span>
                <div>
                  <strong>Cap Rate Defaulted to 7%</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
                    {results.warnings.map((w, i) => (
                      <span key={w.zip_code}>
                        ZIP {w.zip_code}: {w.reason === 'missing market data' ? 'missing market data' : `invalid value (${w.original_value}%)`}
                        {i < results.warnings.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </p>
                </div>
              </WarningBanner>
            )}
            <ResultsHeader>
              <ResultsTitle>Properties Meeting {requiredCOC}% COC Threshold</ResultsTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResultsCount>
                  {results.passed_properties_count || 0} of {results.total_properties_count || 0} properties passed
                </ResultsCount>
                {results.passed_properties && results.passed_properties.length > 0 && (
                  <div style={{ position: 'relative' }}>
                    <ExportButton onClick={() => setShowExportDropdown(!showExportDropdown)}>
                      <IoDownloadOutline /> Export
                    </ExportButton>
                    {showExportDropdown && (
                      <ExportDropdown>
                        <ExportCheckbox>
                          <input 
                            id="zip-export-include-details"
                            name="zip-export-include-details"
                            type="checkbox" 
                            checked={exportIncludeDetails} 
                            onChange={(e) => setExportIncludeDetails(e.target.checked)} 
                          />
                          <label htmlFor="zip-export-include-details">Include Property Details</label>
                        </ExportCheckbox>
                        <ExportCheckbox>
                          <input 
                            id="zip-export-include-inputs"
                            name="zip-export-include-inputs"
                            type="checkbox" 
                            checked={exportIncludeInputs} 
                            onChange={(e) => setExportIncludeInputs(e.target.checked)} 
                          />
                          <label htmlFor="zip-export-include-inputs">Include Input Data</label>
                        </ExportCheckbox>
                        <ExportConfirmButton onClick={handleExport}>
                          Download Excel
                        </ExportConfirmButton>
                      </ExportDropdown>
                    )}
                  </div>
                )}
              </div>
            </ResultsHeader>

            {results.passed_properties && results.passed_properties.length > 0 ? (
              <ResultsTable>
                <ResultsTableHead>
                  <tr>
                    <th onClick={() => handleSort('address')}>
                      Address
                      <SortIndicator $active={sortConfig.key === 'address'}>
                        {sortConfig.key === 'address' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                      </SortIndicator>
                    </th>
                    <th onClick={() => handleSort('list_price')}>
                      Price
                      <SortIndicator $active={sortConfig.key === 'list_price'}>
                        {sortConfig.key === 'list_price' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                      </SortIndicator>
                    </th>
                    {propertyType === 'multi_family' ? (
                      <>
                        <th onClick={() => handleSort('unit_count')}>
                          Units
                          <SortIndicator $active={sortConfig.key === 'unit_count'}>
                            {sortConfig.key === 'unit_count' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th>Unit Breakdown</th>
                        <th onClick={() => handleSort('sqft')}>
                          Bldg Sqft
                          <SortIndicator $active={sortConfig.key === 'sqft'}>
                            {sortConfig.key === 'sqft' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th onClick={() => handleSort('total_rent')}>
                          Total Rent
                          <SortIndicator $active={sortConfig.key === 'total_rent'}>
                            {sortConfig.key === 'total_rent' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th onClick={() => handleSort('dqs')} title="Deal Quality Score - higher = better deal (CapEx done, motivated seller, below market)">
                          DQS
                          <SortIndicator $active={sortConfig.key === 'dqs'}>
                            {sortConfig.key === 'dqs' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                      </>
                    ) : (
                      <>
                        <th onClick={() => handleSort('beds')}>
                          Beds
                          <SortIndicator $active={sortConfig.key === 'beds'}>
                            {sortConfig.key === 'beds' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th onClick={() => handleSort('baths')}>
                          Baths
                          <SortIndicator $active={sortConfig.key === 'baths'}>
                            {sortConfig.key === 'baths' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th onClick={() => handleSort('sqft')}>
                          Sqft
                          <SortIndicator $active={sortConfig.key === 'sqft'}>
                            {sortConfig.key === 'sqft' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th onClick={() => handleSort('estimated_rent')}>
                          Rent
                          <SortIndicator $active={sortConfig.key === 'estimated_rent'}>
                            {sortConfig.key === 'estimated_rent' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                        <th onClick={() => handleSort('dqs')} title="Deal Quality Score - higher = better deal (CapEx done, motivated seller, below market)">
                          DQS
                          <SortIndicator $active={sortConfig.key === 'dqs'}>
                            {sortConfig.key === 'dqs' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                          </SortIndicator>
                        </th>
                      </>
                    )}
                    <th onClick={() => handleSort('coc_percentage_at_year')}>
                      COC %
                      <SortIndicator $active={sortConfig.key === 'coc_percentage_at_year'}>
                        {sortConfig.key === 'coc_percentage_at_year' && sortConfig.direction === 'asc' ? <IoCaretUp size={12} /> : <IoCaretDown size={12} />}
                      </SortIndicator>
                    </th>
                  </tr>
                </ResultsTableHead>
                <ResultsTableBody>
                  {getSortedProperties().map((prop) => (
                    <React.Fragment key={prop.property_id}>
                      <tr>
                        <td>
                          <AddressLink href={getZillowUrl(prop.address)} target="_blank" rel="noopener noreferrer">
                            {prop.address}
                          </AddressLink>
                        </td>
                        <td>
                          <div>{formatCurrency(prop.list_price)}</div>
                          {(prop.price_drop_pct || calculateDOM(prop.list_date) !== null) && (
                            <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                              {prop.price_drop_pct && (
                                <span style={{ color: '#e74c3c', fontWeight: 500, marginRight: '6px' }}>
                                  🔻{prop.price_drop_pct}%
                                </span>
                              )}
                              {calculateDOM(prop.list_date) !== null && (
                                <span style={{ 
                                  color: calculateDOM(prop.list_date) > 60 ? '#e74c3c' : 
                                         calculateDOM(prop.list_date) > 30 ? '#f39c12' : '#7f8c8d',
                                  fontWeight: 500
                                }}>
                                  {calculateDOM(prop.list_date)}d
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        {propertyType === 'multi_family' ? (
                          <>
                            <td>{prop.unit_count || 2}</td>
                            <td>{formatUnitBreakdown(prop.units)}</td>
                            <td>{prop.sqft?.toLocaleString() || '-'}</td>
                            <td>{formatCurrency(prop.total_rent)}/mo</td>
                            <td style={{ 
                              fontWeight: 600,
                              color: (() => {
                                const mktData = marketData[prop.zip_code] || marketData[Object.keys(marketData)[0]];
                                const dqs = calculateDQS(prop, mktData);
                                return dqs >= 70 ? '#27ae60' : 
                                       dqs >= 50 ? '#f39c12' : 
                                       dqs >= 30 ? '#e67e22' : '#e74c3c';
                              })()
                            }}>
                              {(() => {
                                const mktData = marketData[prop.zip_code] || marketData[Object.keys(marketData)[0]];
                                return calculateDQS(prop, mktData);
                              })()}
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{prop.beds || '-'}</td>
                            <td>{prop.baths || '-'}</td>
                            <td>{prop.sqft?.toLocaleString() || '-'}</td>
                            <td>
                              {(() => {
                                const zipData = marketData[prop.zip_code];
                                const rentPerSqft = zipData?.rentPerSqft;
                                if (rentPerSqft && prop.sqft) {
                                  return `$${Math.round(rentPerSqft * prop.sqft).toLocaleString()}/mo`;
                                }
                                return '-';
                              })()}
                            </td>
                            <td style={{ fontWeight: 600 }}>
                              {calculateDQS(prop, marketData[prop.zip_code])}
                            </td>
                          </>
                        )}
                        <td style={{ fontWeight: 600, color: theme.colors.successColor }}>
                          <div>{prop.coc_percentage_at_year}%</div>
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                            <DetailsButton 
                              $expanded={expandedPropertyId === prop.property_id}
                              onClick={() => togglePropertyDetails(prop.property_id)}
                              title="Financial Details"
                            >
                              <IoBarChartOutline size={14} />
                            </DetailsButton>
                            <DetailsButton 
                              $expanded={insightsPropertyId === prop.property_id}
                              onClick={() => toggleInsightsPanel(prop.property_id)}
                              title="Property Insights"
                            >
                              <IoSearchOutline size={14} />
                            </DetailsButton>
                          </div>
                        </td>
                      </tr>
                      {expandedPropertyId === prop.property_id && renderPropertyDetails(prop)}
                      {insightsPropertyId === prop.property_id && renderInsightsPanel(prop)}
                    </React.Fragment>
                  ))}
                </ResultsTableBody>
              </ResultsTable>
            ) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                No properties found meeting the {requiredCOC}% COC threshold. Try adjusting your parameters.
              </p>
            )}

            <p style={{ fontSize: '13px', color: '#666', marginTop: '16px', textAlign: 'center' }}>
              Adjust any section above and re-run to update results
            </p>
          </ResultsContainer>
        </AccordionSection>
      )}

      {/* Query Limit Modal */}
      {showQueryLimitModal && (
        <ModalBackgroundStyled onClick={() => setShowQueryLimitModal(false)}>
          <QueryLimitModalContent onClick={(e) => e.stopPropagation()}>
            <QueryLimitTitle>
              {noSubscription ? 'Free Queries Used Up' : 'No Queries Remaining'}
            </QueryLimitTitle>
            <QueryLimitMessage>
              {noSubscription ? (
                <>
                  You've used all 5 free analyses this month. Upgrade to keep analyzing, or wait for your monthly reset.
                  {freeResetDate && (
                    <>
                      <br /><br />
                      <strong>Your free queries reset on {new Date(freeResetDate).toLocaleDateString('en-US')}</strong>
                    </>
                  )}
                </>
              ) : (
                <>
                  You've used all your analysis queries for this billing period.
                  {getResetInfo() && (
                    <>
                      <br /><br />
                      <strong>Your plan resets on {getResetInfo().date}</strong> with {getResetInfo().queries} queries.
                    </>
                  )}
                </>
              )}
            </QueryLimitMessage>
            <QueryLimitButtonGroup>
              <QueryLimitButton onClick={() => setShowQueryLimitModal(false)}>
                {noSubscription ? 'Maybe Later' : 'Wait for Reset'}
              </QueryLimitButton>
              <QueryLimitButton primary onClick={async () => {
                try {
                  if (noSubscription) {
                    // No subscription - go to signup to choose a plan
                    setShowQueryLimitModal(false);
                    navigate('/signup');
                  } else {
                    // Has subscription - open billing portal to upgrade
                    const cust = await getCustomer(user.uid);
                    console.log('Customer data:', cust);
                    if (cust?.stripeId) {
                      await manageSubscription(cust.stripeId);
                    } else {
                      console.error('No stripeId found:', cust);
                      alert('Unable to load billing portal. Please try again or go to Profile to manage your subscription.');
                    }
                  }
                } catch (err) {
                  console.error('Error:', err);
                  alert('An error occurred. Please try again.');
                }
                setShowQueryLimitModal(false);
              }}>
                {noSubscription ? 'Choose a Plan' : 'Upgrade Plan'}
              </QueryLimitButton>
            </QueryLimitButtonGroup>
          </QueryLimitModalContent>
        </ModalBackgroundStyled>
      )}
    </PageContainer>
  );
};

export default ZipModePage;
